import React, { useEffect, useMemo, useRef, useState } from "react";
import Axios from "axios";
import { useNavigate } from "react-router-dom";
import { useReadyQcEmail } from "../helpers/qcEmail";
import FyiMessages from "./FyiMessages";
import gridProjectBg from "../assets/grid-project-bg.png";
import sfdBrand from "../assets/sfd-brand.png";
import sfdEmblem from "../assets/sfd-emblem.png";

// DEPARTURE uses the server clock in Lima, never the browser clock.
function departureSection(row) {
  const status = String(row?.jobsta || "").trim().toLowerCase();
  if (["not assigned", "assigned", "in process", "correction"].includes(status)) return "progress";
  if (["ready for qc", "job ready"].includes(status)) return "completed";
  return null;
}
function departureBaseId(value) {
  return String(value ?? "").split("-")[0].trim();
}
function formatDepartureReport(data) {
  const stamp = /^(\d{4}-\d{2}-\d{2}) (\d{2}):(\d{2}):/.exec(String(data.generated_at || ""));
  if (!stamp) throw new Error("Fecha del reporte inválida.");
  const hour = Number(stamp[2]);
  const ampm = hour < 12 ? "AM" : "PM";
  const displayHour = String(hour % 12 || 12).padStart(2, "0");
  const clean = value => String(value ?? "").replace(/[\r\n\t]+/g, " ").trim();
  const groups = new Map();
  for (const user of data.users || []) {
    const name = clean(user);
    if (name) groups.set(name, { progress: [], completed: [] });
  }
  const pending = new Set(["not assigned", "assigned", "in process", "correction"]);
  const seen = new Set();
  for (const row of data.rows || []) {
    const id = clean(row.jobid);
    const status = clean(row.jobsta).toLowerCase();
    if (!id || seen.has(id.toLowerCase())) continue;
    const section = departureSection(row);
    if (!section) continue;
    if (section === "completed" && !data.selection_final && !data.report_tracking) {
      const date = status === "job ready" ? row.joblastreadydate : row.jobfirstqcdate;
      if (!date || date < data.start || date >= data.end || date > data.generated_at) continue;
    }
    seen.add(id.toLowerCase());
    const user = clean(row.jobuser);
    if (!groups.has(user)) groups.set(user, { progress: [], completed: [] });
    groups.get(user)[section].push(`${clean(row.jobti)} - ${departureBaseId(id)}`);
  }
  const lines = [`DEPARTURE_${stamp[1]}   ${displayHour}:${stamp[3]} ${ampm}`, "", "COMMENTS:", ""];
  const names = [...groups.keys()].sort((a, b) => a.localeCompare(b, "en", { sensitivity: "base" }));
  for (const user of names) {
    const items = groups.get(user);
    lines.push(`User ${user}`);
    if (items.progress.length) lines.push("IN PROGRESS:", ...items.progress);
    else lines.push("", "No pending Jobs");
    if (items.progress.length || items.completed.length) lines.push("", "COMPLETED (Ready for QC): ", ...items.completed);
    lines.push("", "***************************", "");
  }
  return { text: lines.join("\r\n") + "\r\n", filename: `DEPARTURE_${stamp[1]}_${displayHour}${stamp[3]}${ampm}.txt` };
}

/* ================== Axios base ================== */
Axios.defaults.baseURL = "https://backend.sansfrontieres-design.com";
Axios.defaults.withCredentials = false;

/* =============== Icon helper (inline SVG, no deps) =============== */
const Icon = ({ name, size = 14, style }) => {
  const common = { width: size, height: size, viewBox: "0 0 24 24", fill: "currentColor", style: { verticalAlign: "-2px", marginRight: 6, ...(style||{}) }, "aria-hidden": true };
  switch (name) {
    case "envelope": return (<svg {...common}><rect x="3" y="5" width="18" height="14" rx="2" fill="none" stroke="currentColor" strokeWidth="1.8"/><path d="M4 6l8 6 8-6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/></svg>);
    case "moon": return (<svg {...common}><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>);
    case "plus": return (<svg {...common}><path d="M12 5c.552 0 1 .448 1 1v5h5c.552 0 1 .448 1 1s-.448 1-1 1h-5v5c0 .552-.448 1-1 1s-1-.448-1-1v-5H6c-.552 0-1-.448-1-1s.448-1 1-1h5V6c0-.552.448-1 1-1z"/></svg>);
    case "download": return (<svg {...common}><path d="M12 3a1 1 0 0 1 1 1v8.586l2.293-2.293a1 1 0 1 1 1.414 1.414l-4.004 4.004a1 1 0 0 1-1.414 0L7.286 11.707a1 1 0 1 1 1.414-1.414L11 12.586V4a1 1 0 0 1 1-1z"/><path d="M5 20a1 1 0 0 1 0-2h14a1 1 0 1 1 0 2H5z"/></svg>);
    case "refresh": return (
      <svg {...common}>
        <path d="M12 6V3L7 8l5 5V9a5 5 0 1 1-4.9 6H5a7 7 0 1 0 7-9z" />
      </svg>
    );
    case "bell": return (<svg {...common}><path d="M12 3a4 4 0 0 0-4 4v1.9c0 .5-.2 1-.6 1.4L6 12v1h12v-1l-1.4-1.7c-.4-.4-.6-.9-.6-1.4V7a4 4 0 0 0-4-4z"/><path d="M10 18a2 2 0 0 0 4 0h-4z"/></svg>);
    case "history": return (<svg {...common}><path d="M12 5a7 7 0 1 1-6.32 4H3a1 1 0 1 1 0-2h3a1 1 0 0 1 1 1v3a1 1 0 1 1-2 0v-1.26A5 5 0 1 0 12 7a1 1 0 0 1 0-2z"/><path d="M12 8a1 1 0 0 1 1 1v3.382l1.447.724a1 1 0 1 1-.894 1.788l-2-1A1 1 0 0 1 11 13V9a1 1 0 0 1 1-1z"/></svg>);
    case "filter-off": return (<svg {...common}><path d="M3.707 2.293a1 1 0 0 1 1.414 0l15.586 15.586a1 1 0 0 1-1.414 1.414l-3.586-3.586V21a1 1 0 0 1-1.447.894l-4-2A1 1 0 0 1 10 19v-5.293L3.707 3.707a1 1 0 0 1 0-1.414z"/><path d="M20.293 3.707a1 1 0 0 1 0 1.414L15 10.414V13a1 1 0 0 1-.553.894l-1.447.724V10.414L4.293 2.707a1 1 0 0 1 1.414-1.414l13.172 13.172 1.414-1.414L7.414 1.293z" opacity=".35"/></svg>);
    case "trash": return (<svg {...common}><path d="M9 3a1 1 0 0 0-1 1v1H5a1 1 0 1 0 0 2h14a1 1 0 1 0 0-2h-3V4a1 1 0 0 0-1-1H9z"/><path d="M7 9a1 1 0 0 1 1 1v7a1 1 0 1 1-2 0v-7a1 1 0 0 1 1-1zm5 0a1 1 0 0 1 1 1v7a1 1 0 1 1-2 0v-7a1 1 0 0 1 1-1zm6 0a1 1 0 0 1 1 1v7a3 3 0 0 1-3 3H8a3 3 0 0 1-3-3v-7a1 1 0 0 1 2 0v7a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-7a1 1 0 0 1 1-1z"/></svg>);
    case "logout": return (<svg {...common}><path d="M13 4a1 1 0 1 0-2 0v6H7a1 1 0 1 0 0 2h4v6a1 1 0 1 0 2 0V4z"/><path d="M17 7a1 1 0 0 1 1 1v8a1 1 0 1 1-2 0V8a1 1 0 0 1 1-1z"/></svg>);
    case "star-off": return (<svg {...common}><path d="M12 4.5l2.09 4.23 4.67.68-3.38 3.3.8 4.66L12 15.9l-4.18 2.2.8-4.66-3.38-3.3 4.67-.68L12 4.5z" opacity=".35"/><path d="M3.707 2.293a1 1 0 0 1 1.414 0l15.586 15.586a1 1 0 1 1-1.414 1.414L3.707 3.707a1 1 0 0 1 0-1.414z"/></svg>);
    case "excel": return (<svg {...common}>
  <rect x="2" y="4" width="16" height="16" rx="3" ry="3" fill="currentColor"/>
  <path d="M7.5 9.5l5 5m0-5l-5 5" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
</svg>);case "invoice": return (<svg {...common}>
  <path d="M6 3h10a1 1 0 0 1 1 1v14l-1.5-1-1.5 1-1.5-1-1.5 1-1.5-1-1.5 1-1.5-1L6 19V4a1 1 0 0 1 1-1z" fill="currentColor" opacity=".3"/>
  <path d="M8 6h6a1 1 0 1 1 0 2H8a1 1 0 0 1 0-2zm0 3h6a1 1 0 1 1 0 2H8a1 1 0 0 1 0-2zm0 3h4a1 1 0 1 1 0 2H8a1 1 0 0 1 0-2z" fill="currentColor"/>
</svg>);
    default: return null;
  }
};


/* ================== Helpers HTTP ================== */
const postJSON = (url, payload) =>
  Axios.post(url, payload, { headers: { "Content-Type": "application/json" } });

// Write-helper: igual a postJSON, pero añade __no_history para bloquear el historial en backend
const postWriteJSON = (url, payload) =>
  Axios.post(url, { ...(payload || {}), __no_history: 1 }, { headers: { "Content-Type": "application/json" } });


function getMyWorkId(){
  try{
    const w = JSON.parse(sessionStorage.getItem("loggedWorker") || localStorage.getItem("loggedWorker") || "{}");
    if (w.workid || w.Workid) return String(w.workid || w.Workid).trim();
  
} catch(e) {}
  return "";
}
Axios.defaults.headers.common["X-Login-User"] = getMyWorkId();

/* ================== Helpers de fecha ================== */
function nowSql() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const mi = pad(d.getMinutes());
  const ss = pad(d.getSeconds());
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
}

// === Lima (Perú) - generar timestamp SQL SIEMPRE en hora Lima ===
function nowSqlLima() {
  // "sv-SE" devuelve formato tipo "YYYY-MM-DD HH:MM:SS"
  try {
    return new Date()
      .toLocaleString("sv-SE", { timeZone: "America/Lima", hour12: false })
      .replace("T", " ")
      .replace(",", "");
  } catch {
    // Fallback: si el navegador no soporta timeZone, usa el local (mejor que romper)
    return nowSql();
  }
}


// === Lima (Perú) timezone helpers (solo para mostrar fechas) ===
function parseDateAssumingLima(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  const s = String(value).trim();
  // SQL: YYYY-MM-DD HH:MM(:SS)  → asumir que ya está en hora Lima (UTC-5)
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/);
  if (m) {
    const [, Y, M, D, h = "00", mi = "00", se = "00"] = m;
    // Convertir "Lima local" a un instante UTC (Lima = UTC-5 → sumar 5h)
    const utcMs = Date.UTC(+Y, +M - 1, +D, (+h) + 5, +mi, +se);
    return new Date(utcMs);
  }
  const dt = new Date(s);
  return isNaN(dt) ? null : dt;
}

function formatDateTimeLima(value) {
  if (!value) return "";
  const dt = parseDateAssumingLima(value);
  if (!dt) return String(value);
  try {
    return dt.toLocaleString(undefined, { timeZone: "America/Lima" });
  } catch {
    return dt.toLocaleString();
  }
}

function formatDateTimeHMLima(value) {
  if (!value) return "";
  const dt = parseDateAssumingLima(value);
  if (!dt) return String(value);
  const base = { year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "2-digit" };
  try {
    return dt.toLocaleString(undefined, { ...base, timeZone: "America/Lima" });
  } catch {
    return dt.toLocaleString(undefined, base);
  }
}
function formatDateTime(value) {
  if (!value) return "";
  const dt = new Date(value);
  if (isNaN(dt)) {
    const m = String(value).match(
      /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}):(\d{2}))?$/
    );
    if (!m) return String(value);
    const [, Y, M, D, h = "00", i = "00", s = "00"] = m;
    const dt2 = new Date(
      Number(Y),
      Number(M) - 1,
      Number(D),
      Number(h),
      Number(i),
      Number(s)
    );
    if (isNaN(dt2)) return String(value);
    return dt2.toLocaleString();
  }
  return dt.toLocaleString();
}
function formatDateTimeHM(value) {
  if (!value) return "";
  const toHM = (d) => d.toLocaleString(undefined, {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });
  const dt = new Date(value);
  if (!isNaN(dt)) return toHM(dt);
  const m = String(value).match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/
  );
  if (!m) return String(value);
  const [, Y, M, D, h = "00", i = "00"] = m;
  const dt2 = new Date(Number(Y), Number(M) - 1, Number(D), Number(h), Number(i), 0);
  if (isNaN(dt2)) return String(value);
  return toHM(dt2);
}


/* ============== Utils export ============== */
function escapeHtml(s) {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// Hr Extras: normalize only this field; existing numeric columns keep their behavior.
function normalizeExtraHours(value) {
  const raw = String(value ?? "").trim();
  if (raw === "") return "0";
  if (!/^(?:[0-9]+(?:[.,][0-9]+)?|[.,][0-9]+)$/.test(raw)) {
    throw new Error("Ingresa solo números no negativos. Usa punto (.) o coma (,) como separador decimal.");
  }
  const [whole, fraction = ""] = raw.replace(",", ".").split(".");
  const integer = whole.replace(/^0+/, "") || "0";
  const decimal = fraction.replace(/0+$/, "");
  return integer + (decimal ? "." + decimal : "");
}

// Display two decimal places without changing the stored value.
function formatExtraHours(value) {
  if (value === null || value === undefined || String(value).trim() === "") return "";
  try {
    const [whole, fraction = ""] = normalizeExtraHours(value).split(".");
    return whole + "." + fraction.padEnd(2, "0");
  } catch {
    return String(value);
  }
}

function GridPage() {


  
  const navigate = useNavigate();
  
  const [darkMode, setDarkMode] = useState(false);
  const [freezeColumns, setFreezeColumns] = useState(false);

  
  const isEditingRef = useRef(false);
const scrollRef = useRef(null);
useEffect(() => {
    const cls = 'dark-mode';
    document.body.classList.toggle(cls, darkMode);
    return () => { document.body.classList.remove(cls); };
  }, [darkMode]);
// [REMOVED] Poll fetchRecords interval per user request

  // [REMOVED] Poll digest interval per user request

  // Password dialog sin autoguardado/autofill
  const showInvoicePasswordDialog = () =>
    new Promise((resolve) => {
      const overlay = document.createElement("div");
      overlay.style.cssText =
        "position:fixed;inset:0;background:rgba(0,0,0,.35);display:flex;align-items:center;justify-content:center;z-index:1050;";
      const card = document.createElement("div");
      card.className = "card shadow";
      card.style.width = "360px";

      const rnd = Math.random().toString(36).slice(2);

      card.innerHTML = `
        <div class="card-header"><strong>Área de Invoice</strong></div>
        <form id="__invoice_form" autocomplete="off">
          <div class="card-body">
            <label class="form-label">Password</label>
            <input type="password"
                   class="form-control"
                   id="__invoice_pwd_input"
                   name="pw-${rnd}"
                   autocomplete="off"
                   autocapitalize="off"
                   autocorrect="off"
                   spellcheck="false"
                   data-lpignore="true" data-1p-ignore="true" />
          </div>
          <div class="card-footer d-flex justify-content-end gap-2">
            <button type="button" class="btn btn-secondary btn-sm" id="__invoice_pwd_cancel">Cancelar</button>
            <button type="submit" class="btn btn-primary btn-sm" id="__invoice_pwd_ok">Entrar</button>
          </div>
          
        </form>
      `;

      overlay.appendChild(card);
      document.body.appendChild(overlay);

      const form = card.querySelector("#__invoice_form");
      const input = card.querySelector("#__invoice_pwd_input");
      const cancelBtn = card.querySelector("#__invoice_pwd_cancel");
      const cleanup = () => overlay.remove();

      input && input.focus();

      form.addEventListener("submit", (e) => {
        e.preventDefault();
        const v = input ? input.value : "";
        cleanup();
        resolve(v);
      });

      cancelBtn.addEventListener("click", () => {
        cleanup();
        resolve(null);
      });

      overlay.addEventListener("click", (e) => {
        if (e.target === overlay) {
          cleanup();
          resolve(null);
        }
      });

      input.addEventListener("keydown", (e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          cleanup();
          resolve(null);
        }
      });
    });

  // AUTO: Clear filters al cargar (usa window.* si existen)
  useEffect(() => {
    setTimeout(() => {
      const g = typeof window !== "undefined" ? window : {};
      const clearFn =
        typeof clearFilters === "function"
          ? clearFilters
          : typeof g.unfilter === "function"
          ? g.unfilter
          : typeof g.onUnfilter === "function"
          ? g.onUnfilter
          : () => {};
      clearFn();
      document.querySelectorAll("select").forEach((s) => {
        const o = [...s.options].find((x) => /^\s*all\s*$/i.test(x.text));
        if (o) {
          s.value = o.value;
          s.dispatchEvent(new Event("change", { bubbles: true }));
        }
      });
    }, 0);
  }, []);

  const [records, setRecords] = useState([]);

// === One-time initial sort by EP Close Date (DESC) *only* after login (per tab) ===
const __onceSortRef = useRef(false);
useEffect(() => {
  if (__onceSortRef.current) return;
  if (!Array.isArray(records) || records.length === 0) return;
  try {
    if (sessionStorage.getItem("gridLoginSortPending") === "1" &&
        sessionStorage.getItem("gridInitSortDone") !== "1") {
      const ts = (v) => {
        if (!v) return -Infinity;
        const d = new Date(v);
        if (!isNaN(d)) return d.getTime();
        const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/);
        if (!m) return -Infinity;
        const [,Y,M,D,h="00",i="00",s="00"] = m;
        const dt = new Date(Number(Y), Number(M)-1, Number(D), Number(h), Number(i), Number(s));
        return isNaN(dt) ? -Infinity : dt.getTime();
      };
      setRecords((curr) => {
        const arr = Array.isArray(curr) ? [...curr] : [];
        arr.sort((a,b) => ts(b?.jobclodate) - ts(a?.jobclodate));
        return arr;
      });
      sessionStorage.setItem("gridInitSortDone", "1");
    }
  } catch {}
  __onceSortRef.current = true;
  try { sessionStorage.removeItem("gridLoginSortPending"); } catch {}
}, [records]);

  
// ==== QC Bell state ====
const [qcCount, setQcCount] = useState(0);
const [qcList, setQcList] = useState([]);           // UI (post-it/campanita): { id, project, time }
const [qcEmailRows, setQcEmailRows] = useState([]); // Email: filas completas (con jobid, jobsta, etc.)

// Email: SOLO si la campanita > 0 (qcCount). El filtro de Job ID válido está en qcEmail.js
useReadyQcEmail(qcEmailRows, Axios);
const [showQcPostit, setShowQcPostit] = useState(false);
  const [flashRowId, setFlashRowId] = useState("")
  /* qcAlerts mount sync */
  useEffect(() => {
    const read = () => {
      try {
        let arr = JSON.parse(localStorage.getItem("qcAlerts") || "[]");
        if (!Array.isArray(arr)) arr = [];
        const norm = arr
          .map(x => (typeof x === 'string' ? { id: x, project: '' } : x))
          .filter(x => String(x?.id ?? '').trim() !== '');
        setQcList(norm);
        setQcCount(norm.length);
      } catch {
        setQcList([]);
        setQcCount(0);
      }
    };
    read();
    const onStorage = (e) => { if (!e || e.key === "qcAlerts") read(); };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);
;

  const [loggedUser, setLoggedUser] = useState(() => {
    try {
      const wr = localStorage.getItem("loggedWorker");
      const w = wr ? JSON.parse(wr) : null;
      return w?.workid || localStorage.getItem("loggedUser") || "";
    } catch {
      return localStorage.getItem("loggedUser") || "";
    }
  });
  const [selectedRows, setSelectedRows] = useState([]);
  const [departureSession, setDepartureSession] = useState(null);
  const [departureDialog, setDepartureDialog] = useState(false);
  const [departureHint, setDepartureHint] = useState(false);
  useEffect(() => {
    if (!departureHint) return;
    const timer = window.setTimeout(() => setDepartureHint(false), 10000);
    return () => window.clearTimeout(timer);
  }, [departureHint]);
  const [favoriteRows, setFavoriteRows] = useState([]);
  const [isCreating, setIsCreating] = useState(false);

  const [toasts, setToasts] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showArchived, setShowArchived] = useState(true);
  const pushToast = (msg, variant = "success") => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t.slice(-1), { id, msg, variant }]);

  //  setToasts((t) => [...t, { id, msg, variant }]);
   // setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3000);
 
  };

  // Manual close for toasts (no timers)
  const closeToast = (id) => setToasts((t) => t.filter((x) => x.id !== id));

 
  const statusOptions = [
    "Not Assigned",
    "Assigned",
    "In Process",
    "In Hold",
    "Correction",
    "Ready for QC",        
    "Billing",
    "Payed",
    "Archived",
    "Job Ready",

  ];
  const userOptions = ["Marco", "Naara", "Pilar", "Alessandra", "Elizet", "Nelson", "Ibet"];
  const typeOptions = [
    "Coax Asbuilt",
    "Coax Asb Revert Split",
    "Coax Asb Revert N+0",
    "Coax Design",
    "Coax Bentley",
    "Coax Bentley Asb",
    "Coax Bentley Des",
    "Fiber Bentley Asb",
    "Fiber Bentley Des",
    "Route",
    "Route FTTP",
    "Fiber FTTP",
    "Fiber Design BAU",
    "Fiber Asbuilt BAU",
    "Fiber Iris",
    "Iris Split Fiber Design",
    "Iris Split Coax Design",
    "Iris Split Fiber Asbuilt",
    "Iris Split Coax Asbuilt",
    "Bentley Split Fiber Design",
    "Bentley Split Coax Design",
    "Bentley Split Fiber Asbuilt",
    "Bentley Split Coax Asbuilt",
    "Iris-Splits",
    "Other Tasks",
  ];
  const closeEpOptions = ["Opened EP", "Closed EP", "HOT Priority"];
  const projectOptions = [
    "Project-01",
    "Project-02",
    "Project-03",
    "Project-04",
    "Project-05",
    "Project-06",
    "Project-07",
    "Project-08",
    "Project-09",
    "Project-10",
    "Project-11",
    "Project-12",
  ];

  const [filters, setFilters] = useState({
    jobid: "",
    status: "",
    user: "",
    type: "",
    closeep: "",
    project: "",
    notesadmin: "",
    notes: "",
    proname: "",
    proport: "",
    ou: "",
    lob: "",
    comty: "",
    routeaerial: "",
    routeug: "",
    coaxae: "",
    coaxug: "",
    asbae: "",
    asbug: "",
    lode: "",
    hc: "",
  });

  
  // Header highlight helper (solo estilo)
  const isHeaderFilterActive = (key) => {
    if (key === "status") return v2arr(filters.status).length > 0;
    const v = filters[key];
    return v !== undefined && v !== null && String(v).trim() !== "";
  };
const [editingCell, setEditingCell] = useState(null); // { id, field }
  const [editValue, setEditValue] = useState("");
  // --- Popover de Status (UI) ---
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const statusMenuRef = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (!statusMenuRef.current?.contains(e.target)) setShowStatusMenu(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Acepta string o array
  const v2arr = (v) => (Array.isArray(v) ? v : (v ? [v] : []));


  const normalize = (v) => (v ?? "").toString().toLowerCase().replace(/[\s-]+/g, "");
  const toId = (v) => (v === null || v === undefined ? "" : String(v));

  // De "Project-08" | "Project08" → "Project 08"
  const workIdToProjectLabel = (workid) => {
    if (!workid) return "";
    const s = String(workid).trim();
    const m = s.match(/project[-_\s]*0*(\d+)/i);
    if (m) return `Project ${String(m[1]).padStart(2, "0")}`;
    return s;
  };

  const isTempId = (val) => {
    if (!val) return false;
    const s = String(val).trim().toLowerCase();
    return /^jobid\s*nuevo-\d+$/.test(s);
  };
  const tempIndexFromId = (val) => {
    if (!val) return 0;
    const m = String(val).trim().toLowerCase().match(/^jobid\s*nuevo-(\d+)$/);
    return m ? parseInt(m[1], 10) || 0 : 0;
  };
  const nextTempId = () => {
    const max = records.reduce((acc, r) => Math.max(acc, tempIndexFromId(r?.jobid)), 0);
    return `JobID nuevo-${max + 1}`;
  };

  const openQcBell = () => setShowQcPostit(v => !v);
  const markQcRead = () => {
    localStorage.setItem('qcAlerts', '[]');
    setQcList([]); setQcCount(0); setShowQcPostit(false);
  };
  const gotoQcItem = async (jobid) => {
    try {
      if (typeof fetchRecords === 'function') { await fetchRecords(); }
      const id = String(jobid||'');
      setSelectedRows(prev => Array.from(new Set([...(prev||[]), id])));
      setFlashRowId(id);
      setTimeout(()=> setFlashRowId(''), 3000);
 //     setTimeout(()=>{
 //       const sel = document.querySelector(`tr[data-rowid="${String(jobid||'').replace(/"/g,'\"')}"]`);
 //       if (sel && sel.scrollIntoView) sel.scrollIntoView({behavior:'smooth', block:'center'});
 //     }, 60);
    } catch {}
  };
  // Keep scroll position when updating records to avoid jumping to the top
  const setRecordsKeepScroll = (nextOrFn) => {
    try {
      const el = scrollRef.current;
      const st = el ? el.scrollTop : null;
      const sl = el ? el.scrollLeft : null;
      if (typeof nextOrFn === 'function') {
        setRecords((curr) => nextOrFn(curr));
      } else {
        setRecords(nextOrFn);
      }
      if (el != null && st != null) {
        requestAnimationFrame(() => {
          try {
            el.scrollTop = st;
            if (sl != null) el.scrollLeft = sl;
          } catch {}
        });
      }
    } catch {}
  };


const fetchRecords = async () => {
    
    if (isEditingRef.current) return;
try {
      const { data } = await Axios.get("/get_projects.php?include_extra_hours=1&_=" + Date.now());
      if (data?.status === "success") {
const arr = Array.isArray(data.data) ? data.data : [];
setRecordsKeepScroll(arr.slice());

// Limpia selección "fantasma" (localStorage) cuando el jobid ya no existe en el grid
try {
  const visibleSet = new Set(arr.map(r => toId(r?.jobid)));
  setSelectedRows(prev => (prev || []).filter(id => visibleSet.has(toId(id))));
} catch {}
}
} catch (err) {
      console.error("Error al cargar proyectos:", err);
      alert("Error de conexión con el servidor.");
    }
  };

  
  // Envia SMS via backend por cada id nuevo (usa deduplicación local)
  const sendQcSms = async (items) => {
        // DISABLED: evitar correos/sms sin confirmación (QC)
    return;
try {
      const sent = new Set(JSON.parse(localStorage.getItem("qcSmsSent") || "[]"));
      const todo = (items||[]).filter(x => x?.id && !sent.has(String(x.id)));
      for (const it of todo) {
        try {
          await Axios.post("/notify_ready_qc.php", new URLSearchParams({ jobid: String(it.id), jobpro: String(it.project || "") }));
          sent.add(String(it.id));
        } catch (e) { /* silente */ }
      }
      localStorage.setItem("qcSmsSent", JSON.stringify(Array.from(sent)));
    } catch {}
  };
// === Client-side QC detector: mira el grid y avisa por cada fila con Status = "Ready for QC" nueva ===
  const updateQcFromList = (readyList) => {
    try {
      const list = Array.isArray(readyList) ? readyList : [];
      // ids actuales
      const currentIds = new Set(list.map(x => String(x?.id || x?.jobid || "").trim()).filter(x => x !== ""));
      // baseline conocida
      let known = [];
      try { known = JSON.parse(localStorage.getItem("qcKnownReady") || "[]"); } catch { known = []; }
      if (!Array.isArray(known)) known = [];
      const knownSet = new Set(known.map(String));
      // Primera carga: no notificar, sólo establecer baseline
      if (localStorage.getItem("qcKnownReady") === null) {
        localStorage.setItem("qcKnownReady", JSON.stringify(Array.from(currentIds)));
        return;
      }
      // nuevos = actuales que no estaban en known
      const newbies = [...currentIds].filter(id => !knownSet.has(String(id)));
      // Actualiza baseline SIEMPRE (para re-alertar si el status vuelve a Ready for QC)
      localStorage.setItem("qcKnownReady", JSON.stringify(Array.from(currentIds)));
      if (newbies.length === 0) return;
      // mergea con qcAlerts evitando duplicados
      let arr = [];
      try { arr = JSON.parse(localStorage.getItem("qcAlerts") || "[]"); } catch { arr = []; }
      if (!Array.isArray(arr)) arr = [];
      // mergea con qcAlerts (permitir repetir el mismo Job ID si vuelve a Ready for QC)
      const toAdd = [];
      for (const r of list) {
        const id = String(r?.id || r?.jobid || "").trim();
        if (newbies.includes(id)) {
          toAdd.push({ id, project: r?.project || r?.jobpro || "", time: Date.now() });
        }
      }
      if (toAdd.length) {
        // sendQcSms(toAdd); // disabled (avoid unconfirmed emails)
        const merged = [...arr, ...toAdd];
        localStorage.setItem("qcAlerts", JSON.stringify(merged));
        try { window.dispatchEvent(new StorageEvent("storage", { key: "qcAlerts", newValue: JSON.stringify(merged) })); } catch {}
        const norm = merged.map(x => (typeof x === "string" ? { id: x, project: "" } : x));
        setQcList(norm);
        setQcCount(norm.length);
        setShowQcPostit(true); // mostrar post-it de inmediato
      }
    } catch {}
  };

  // === Server digest of Ready for QC across tracked projects (cross-session) ===
  const fetchQCDigest = async () => {
    try {
      const resp = await Axios.get("/qc_digest.php?_=" + Date.now());
      const list = Array.isArray(resp.data) ? resp.data : (resp.data?.data || []);
      // list items should be { id, project, time? }
      let known = [];
      try { known = JSON.parse(localStorage.getItem("qcKnownReady") || "[]"); } catch { known = []; }
      if (!Array.isArray(known)) known = [];
      const knownSet = new Set(known.map(String));
      const currentIds = new Set(list.map(x => String(x?.id||x?.jobid||"").trim()).filter(x => x !== ""));
      // Primera vez: si no hay baseline, inicializa con todo y no alertes
      if (localStorage.getItem("qcKnownReady") === null) {
        localStorage.setItem("qcKnownReady", JSON.stringify(Array.from(currentIds)));
        return;
      }
      // Nuevos = en digest pero no en known
      const newbies = list.filter(x => !knownSet.has(String(x?.id||x?.jobid||"")));
      // Actualiza baseline SIEMPRE (para detectar transiciones not-ready -> ready de nuevo)
      localStorage.setItem("qcKnownReady", JSON.stringify(Array.from(currentIds)));
      if (newbies.length === 0) return;
      // Actualiza qcAlerts (para UI)
      let arr = [];
      try { arr = JSON.parse(localStorage.getItem("qcAlerts") || "[]"); } catch { arr = []; }
      if (!Array.isArray(arr)) arr = [];
      // permitir repetir el mismo Job ID si vuelve a Ready for QC
      const toAdd = [];
      for (const x of newbies) {
        const id = String(x?.id||x?.jobid||"").trim();
        if (id) {
          toAdd.push({ id, project: x?.project || x?.jobpro || "", time: Date.now() });
        }
      }
      if (toAdd.length) {
        // sendQcSms(toAdd); // disabled (avoid unconfirmed emails)
        const merged = [...arr, ...toAdd];
        localStorage.setItem("qcAlerts", JSON.stringify(merged));
        try { window.dispatchEvent(new StorageEvent("storage", { key: "qcAlerts", newValue: JSON.stringify(merged) })); } catch {}
        // También actualiza inmediatamente el estado local
        try {
          const norm = merged.map(x => (typeof x === "string" ? { id: x, project: "" } : x));
          setQcList(norm); setQcCount(norm.length);
          /* refresh records on newbies */
          try { if (typeof fetchRecords === "function") { fetchRecords(); } } catch {}
        } catch {}
      }
    } catch (e) {
      // silenciar
    }
  };

  // === QC Bell: refresco cross-PC (solo campanita; NO recarga el grid) ===
  useEffect(() => {
    let timer = null;
    let killed = false;

    const tick = async () => {
      if (killed) return;
      try { if (document.hidden) return; } catch {}
      if (isEditingRef.current) return;

      try {
        const { data } = await Axios.get("/get_projects.php?include_extra_hours=1&_=" + Date.now());
        const arr = data?.status === "success"
          ? (Array.isArray(data.data) ? data.data : [])
          : (Array.isArray(data) ? data : []);

        
const readyRows = (arr || [])
  .filter(r => normalize(r?.jobsta) === "readyforqc")
  .map(r => ({ ...r, jobid: String(r?.jobid ?? "").trim() })) // normaliza jobid
  .filter(r => String(r?.jobid ?? "").trim() !== "");

// Para UI campanita/post-it: solo id + project
const ready = readyRows.map(r => ({
  id: String(r?.jobid ?? "").trim(),
  project: String(r?.jobpro ?? "")
}));

// Para email: SOLO los "nuevos" Ready for QC (los mismos que hacen subir la campanita)
// Usamos la baseline guardada en localStorage (qcKnownReady), que también usa updateQcFromList.
try {
  const hasBaseline = localStorage.getItem("qcKnownReady") !== null;
  if (hasBaseline) {
    let known = [];
    try { known = JSON.parse(localStorage.getItem("qcKnownReady") || "[]"); } catch { known = []; }
    if (!Array.isArray(known)) known = [];
    const knownSet = new Set(known.map(String));

    const newbiesRows = readyRows.filter(r => !knownSet.has(String(r?.jobid || "")));
    if (newbiesRows.length) {
      // Email deshabilitado en GridPage (solo campanita/post-it).
    }
  }
} catch {}

updateQcFromList(ready);
} catch {
        // silencio: la campana solo se actualiza si hay conexión
      }
    };

    // primera ejecución y luego cada 10s
    tick();
    timer = setInterval(tick, 10000);

    const onVis = () => { try { if (!document.hidden) tick(); } catch {} };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      killed = true;
      if (timer) clearInterval(timer);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);




  // Inicio de sesión: usa loggedWorker (nuevo) o loggedUser (compat)
  useEffect(() => {
    let userKey = "";
    const wStr = localStorage.getItem("loggedWorker");
    if (wStr) {
      try {
        const w = JSON.parse(wStr);
        if (w && w.workid) {
          setLoggedUser(w.workid);
          userKey = w.workid;
          // Filtro automático por proyecto del worker
          const pLabel = workIdToProjectLabel(w.workid);
          setFilters((f) => ({ ...f, project: pLabel }));
        }
      } catch {}
    }
    if (!userKey) {
      const u = localStorage.getItem("loggedUser") || "";
   if (!u) {
  navigate("/login", { replace: true });
      return; // redirección silenciosa
  return;
  
}
      setLoggedUser(u);
      userKey = u;
    }

    // favoritos/selección del usuario
    try {
      setFavoriteRows(JSON.parse(localStorage.getItem(`favorites:${normalize(userKey)}`)) ?? []);
      setSelectedRows(JSON.parse(localStorage.getItem(`selected:${normalize(userKey)}`)) ?? []);
    } catch {}

    fetchRecords();
    try{ sessionStorage.setItem("gridLoginSortPending","1"); }catch{}
  }, [navigate]);

  useEffect(() => {
    if (!loggedUser) return;
    localStorage.setItem(`favorites:${normalize(loggedUser)}`, JSON.stringify(favoriteRows));
  }, [favoriteRows, loggedUser]);

  useEffect(() => {
    if (!loggedUser) return;
    localStorage.setItem(`selected:${normalize(loggedUser)}`, JSON.stringify(selectedRows));
  }, [selectedRows, loggedUser]);

  const handleCheckboxChange = (e, jobId) => {
    const id = toId(jobId);
    if (departureSession && e.target.checked && !departureSection(records.find(r => toId(r.jobid) === id))) {
      pushToast("Ese estado no se reporta en DEPARTURE.", "warning");
      return;
    }
    if (departureSession && e.target.checked) {
      setDepartureSession(prev => !prev || prev.orderIds.includes(id) ? prev : { ...prev, orderIds: [...prev.orderIds, id] });
    }
    setSelectedRows((prev) =>
      e.target.checked ? Array.from(new Set([...prev, id])) : prev.filter((x) => x !== id)
    );
  };
  const toggleFavorite = (jobId) => {
    const id = toId(jobId);
    setFavoriteRows((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };


  // Bulk update Status for selected rows (Billing/Payed)
  const bulkSetStatus = async (newStatus) => {
    const ids = (selectedRows || []).map((x) => toId(x)).filter(Boolean);
    if (!ids.length) {
      pushToast("Selecciona al menos una fila.", "warning");
      return;
    }
    const idSet = new Set(ids);

    // Optimistic UI update
    setRecords((prev) =>
      prev.map((r) => (idSet.has(toId(r.jobid)) ? { ...r, jobsta: newStatus } : r))
    );

    // Persist to DB
    try {
      for (const id of ids) {
        const savedRow = await persistFieldChange(id, "jobsta", newStatus);
        if (savedRow) setRecords(prev => prev.map(r => toId(r.jobid) === id ? { ...r, ...savedRow } : r));
      }
      pushToast(`Status actualizado a "${newStatus}" para ${ids.length} fila(s).`, "success");
    } catch (err) {
      // If something fails, refresh from backend to keep in sync
      pushToast(err?.message || "Error actualizando Status.", "danger");
      fetchRecords();
    }
  };

const isHiddenStatus = (s) => {
  const t = String(s || '').trim().toLowerCase();
  return t === 'ready for qc' || t === 'hold' || t === 'in hold' || t === 'archived';
};


  const filteredRecords = useMemo(() => {
    const matchText = (value, needle) => !needle || normalize(value).includes(normalize(needle));
    const matchEq = (value, needle) => !needle || normalize(value) === normalize(needle);

    
    const matchMulti = (value, needle) => {
      const arr = v2arr(needle);
      return arr.length === 0 || arr.some((n) => normalize(value) === normalize(n));
    };
return (records || []).filter((r) => {
                                const __st = normalize(r?.jobsta);
                // Ocultar siempre Billing/Payed/Archived (siguen en BD, solo no se muestran en el grid)
                if (__st === "billing" || __st === "payed" || __st === "paid" || __st === "archived") return false;
if (normalize(r?.jobsta) === "invoicing") return false;
if (!showArchived && isHiddenStatus(r?.jobsta)) return false;
if (departureSession?.orderIds.includes(toId(r.jobid))) return true;
const f = filters;
      if (!matchText(r?.jobid, f.jobid)) return false;
      if (!matchMulti(r?.jobsta, f.status)) return false;
      if (!matchEq(r?.jobuser, f.user)) return false;
      if (!matchEq(r?.jobti, f.type)) return false;
      if (!matchEq(r?.jobclo, f.closeep)) return false;
      if (!matchEq(r?.jobpro, f.project)) return false;
      if (!matchText(r?.jobnotesadmin, f.notesadmin)) return false;
      if (!matchText(r?.jobnotes, f.notes)) return false;

      if (!matchText(r?.jobproname, f.proname)) return false;
      if (!matchText(r?.jobproport, f.proport)) return false;
      if (!matchText(r?.jobou, f.ou)) return false;
      if (!matchText(r?.joblob, f.lob)) return false;
      if (!matchText(r?.jobcomty, f.comty)) return false;
      if (!matchText(r?.jobrouteaerial, f.routeaerial)) return false;
      if (!matchText(r?.jobrouteug, f.routeug)) return false;
      if (!matchText(r?.jobcoaxae, f.coaxae)) return false;
      if (!matchText(r?.jobcoaxug, f.coaxug)) return false;
      if (!matchText(r?.jobasbae, f.asbae)) return false;
      if (!matchText(r?.jobasbug, f.asbug)) return false;
      if (!matchText(r?.joblode, f.lode)) return false;
      if (!matchText(r?.jobhc, f.hc)) return false;
      return true;
    });
  }, [records, filters, showArchived, departureSession, selectedRows]);
  // --- Sorting for five columns ---
  const [sortKey, setSortKey] = useState(null); // 'jobsta' | 'jobuser' | 'jobti' | 'jobclo' | 'jobpro'
  const [sortDir, setSortDir] = useState('asc');
  const onSort = (key) => {
    setSortKey((prev) => {
      if (prev === key) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        return prev;
      } else {
        setSortDir('asc');
        return key;
      }
    });
  };
  const sortedRecords = useMemo(() => {
    const arr = [...filteredRecords];
    const reviewRank = new Map((departureSession?.orderIds || []).map((id, index) => [id, index]));
    arr.sort((a, b) => {
      if (departureSession) {
        const ra = reviewRank.get(toId(a.jobid)), rb = reviewRank.get(toId(b.jobid));
        if (ra !== undefined || rb !== undefined) return (ra ?? Infinity) - (rb ?? Infinity);
      }
      if (!sortKey) return 0;
      const va = (a?.[sortKey] ?? '').toString().toLowerCase();
      const vb = (b?.[sortKey] ?? '').toString().toLowerCase();
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filteredRecords, sortKey, sortDir, departureSession, selectedRows]);


  const visibleIds = useMemo(() => filteredRecords.filter(r => !departureSession || departureSection(r)).map(r => toId(r.jobid)), [filteredRecords, departureSession]);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedRows.includes(id));
  const toggleSelectAll = (checked) => {
    if (departureSession && checked) {
      const ids = sortedRecords.filter(departureSection).map(r => toId(r.jobid));
      setDepartureSession(prev => prev ? { ...prev, orderIds: [...prev.orderIds, ...ids.filter(id => !prev.orderIds.includes(id))] } : prev);
    }
    setSelectedRows((prev) => {
      if (checked) return Array.from(new Set([...prev, ...visibleIds.filter(id => !departureSession || departureSection(records.find(r => toId(r.jobid) === id)))]));
      const toRemove = new Set(visibleIds);
      return prev.filter((id) => !toRemove.has(id));
    });
  };

  const onFilterChange = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));
  const clearFilters = () => {
    setFilters({
      jobid: "",
      status: "",
      user: "",
      type: "",
      closeep: "",
      project: "",
      notesadmin: "",
      notes: "",
      proname: "",
      proport: "",
      ou: "",
      lob: "",
      comty: "",
      routeaerial: "",
      routeug: "",
      coaxae: "",
      coaxug: "",
      asbae: "",
      asbug: "",
      lode: "",
      hc: "",
    });

    // IMPORTANT: también limpia selección para evitar "Remove" activo con seleccionados ocultos
    setSelectedRows([]);
  };

  // Submission Date NO editable: NO incluyas "jobdatesub" aquí
  const editableFields = new Set([
    "jobid",
    "jobsta",
    "jobuser",
    "jobti",
    "jobclo",
    "jobpro",
    "jobnotesadmin",
    "jobnotes",
    "jobproname",
    "jobproport",
    "jobou",
    "joblob",
    "jobcomty",
    "jobrouteaerial",
    "jobrouteug",
    "jobcoaxae",
    "jobcoaxug",
    "jobasbae",
    "jobasbug",
    "joblode",
    "jobhc",
    "jbhrex",
  ]);

  const startEdit = (id, field, currentVal) => {
    if (!editableFields.has(field)) return;
    
    isEditingRef.current = true;
let initial = currentVal ?? "";
    if (field === "jobid" && isTempId(currentVal)) initial = "";
    setEditingCell({ id: toId(id), field });
    setEditValue(initial);
  };
  const cancelEdit = () => {
    
    isEditingRef.current = false;
setEditingCell(null);
    setEditValue("");
  };

  const persistFieldChange = async (jobid, field, value) => {
    const jid = String(jobid ?? "").trim();
    if (field === "jobsta") {
      // Solo cuando cambia STATUS permitimos historial
      const payload = { jobid: jid, [field]: value, jobdatesub: nowSqlLima(), __no_history: 0,
        __close_ep_on_qc: normalize(value) === normalize("Ready for QC") ? 1 : 0 };
      const res = await postJSON("/update_project.php", payload);
      if (!res?.data || res.data.status !== "success") {
        throw new Error(res?.data?.message || "Respuesta no exitosa");
      }
      return res.data.row || null;
     } else if (field === "jobclo") {
      const jid2 = String(jobid ?? "").trim();
      const isClosed = /^\s*closed\s*ep\s*$/i.test(String(value || "").trim());
      const payload = { jobid: jid2, jobclo: value, jobdatesub: nowSqlLima() };
      if (isClosed) payload.jobclodate = nowSqlLima();
      const res = await postWriteJSON("/update_project.php", payload);
      if (!res?.data || res.data.status !== "success") {
        throw new Error(res?.data?.message || "Respuesta no exitosa");
      }
      return true;
    } else {
      // Para cualquier otro campo, bloqueamos historial
      const payload = { jobid: jid, [field]: value, jobdatesub: nowSqlLima() };
      const res = await postWriteJSON("/update_project.php", payload);
      if (!res?.data || res.data.status !== "success") {
        throw new Error(res?.data?.message || "Respuesta no exitosa");
      }
      return true;
    }
  };const persistJobIdChange = async (oldId, newId) => {
    const res = await postWriteJSON("/update_jobid.php", {
      old_jobid: String(oldId ?? "").trim(),
      new_jobid: String(newId ?? "").trim(),
    });
    if (!res?.data || res.data.status !== "success") {
      throw new Error(res?.data?.message || "No se pudo actualizar el Job Id");
    }
    return true;
  };
  const persistCreateRecord = async (recordObj) => {
    const res = await postWriteJSON("/create_project.php", recordObj || {});
    if (!res?.data || res.data.status !== "success") {
      throw new Error(res?.data?.message || "Error al crear el registro");
    }
    return res.data.row || recordObj;
  };

  // ⬇️ acepta valor opcional (desde el modal) y úsalo en todo el flujo
  const commitEdit = async (valueOverride) => {
    if (!editingCell) return;
    const { id, field } = editingCell;

    let newVal = valueOverride !== undefined ? valueOverride : editValue;
    if (field === "jbhrex") {
      try { newVal = normalizeExtraHours(newVal); }
      catch (err) { cancelEdit(); alert(err.message); return; }
    }

    const currentRecord = records.find((r) => toId(r.jobid) === id) || {};
    const current = currentRecord?.[field] ?? "";
    if (String(current) === String(newVal)) {
      cancelEdit();
      return;
    }

    const prev = records;
    let next = records.map((r) => (toId(r.jobid) === id ? { ...r, [field]: newVal, ...(field === "jobclo" && /^\s*closed\s*ep\s*$/i.test(String(newVal || "").trim()) ? { jobclodate: nowSqlLima() } : {}) } : r));
    setRecords(next);
// ← desactiva cualquier orden activo para mantener el orden actual

    const wasTemp = isTempId(id);
    const newId = field === "jobid" ? String(newVal).trim() : id;
    const newIsTemp = isTempId(newId);

    // ✅ Bloquear Job ID duplicado (no crear ni dejar fila temporal si ya existe)
    if (field === "jobid" && newId) {
      const dup = (prev || []).some(
        (r) => normalize(r?.jobid) === normalize(newId) && toId(r?.jobid) !== toId(id)
      );
      if (dup) {
        const msg = "Job ID Ya existe... intente con otro nuevo numero de Job ID";
        alert(msg);
        pushToast(msg, "warning");

        // Si era fila temporal (+Add), eliminarla (sin tocar BD)
        if (wasTemp) {
          setRecords((prev || []).filter((r) => toId(r?.jobid) !== toId(id)));
          setSelectedRows((arr) => (arr || []).filter((x) => toId(x) !== toId(id)));
          setFavoriteRows((arr) => (arr || []).filter((x) => toId(x) !== toId(id)));
        } else {
          // Si era edición de una fila real, revertir UI
          setRecords(prev);
        }

        cancelEdit();
        return;
      }
    }

    try {
      if (wasTemp) {
        if (field === "jobid") {
          if (newIsTemp || !newId) {
            if (newId !== id) {
              setSelectedRows((arr) => {
                const s = new Set(arr);
                if (s.delete(id)) s.add(newId);
                return Array.from(s);
              });
              setFavoriteRows((arr) => {
                const s = new Set(arr);
                if (s.delete(id)) s.add(newId);
                return Array.from(s);
              });
            }
          } else {
            const recordAfterEdit =
              next.find((r) => toId(r.jobid) === newId) ||
              next.find((r) => toId(r.jobid) === id);
            const created = await persistCreateRecord(recordAfterEdit);
            const stamped = { ...created, jobdatesub: created.jobdatesub || nowSqlLima(), jobclodate: created.jobclodate || nowSqlLima() };
            next = next.map((r) => (toId(r.jobid) === newId ? { ...r, ...stamped } : r));
            setRecords(next);
            //pushToast("Registro creado.", "success");

            setSelectedRows((arr) => {
              const s = new Set(arr);
              if (s.delete(id)) s.add(newId);
              return Array.from(s);
            });
            setFavoriteRows((arr) => {
              const s = new Set(arr);
              if (s.delete(id)) s.add(newId);
              return Array.from(s);
            });
          }
        } else {
          //pushToast("Cambio aplicado (temporal).", "success");
        }
      } else {
        if (field === "jobid") {
          await persistJobIdChange(id, newId);
          setDepartureSession(prev => prev ? { ...prev,
            autoIds: prev.autoIds.map(x => x === id ? newId : x),
            orderIds: prev.orderIds.map(x => x === id ? newId : x)
          } : prev);
          setRecords((curr) =>
            curr.map((r) =>
              toId(r.jobid) === newId ? { ...r, jobdatesub: nowSqlLima() } : r
            )
          );
          setSelectedRows((arr) => {
            const s = new Set(arr);
            if (s.delete(id)) s.add(newId);
            return Array.from(s);
          });
          setFavoriteRows((arr) => {
            const s = new Set(arr);
            if (s.delete(id)) s.add(newId);
            return Array.from(s);
          });
          pushToast("Job ID actualizado.", "success");
        } else {
          const savedRow = await persistFieldChange(id, field, newVal);
          setRecords((curr) =>
            curr.map((r) => {
              if (toId(r.jobid) !== id) return r;
              const patch = { jobdatesub: nowSqlLima() };
              if (field === "jobclo" && /^\s*closed\s*ep\s*$/i.test(String(newVal || "").trim())) patch.jobclodate = nowSqlLima();
              return { ...r, ...patch, ...(savedRow || {}) };
            })
          );
       //   pushToast("Cambios guardados.", "success");
        }
      }
    } catch (err) {
      alert(`No se pudo guardar el cambio en el servidor.\n\n${err?.message || "Error desconocido"}`);
      setRecords(prev);
      pushToast("No se pudo guardar.", "error");
    } finally {
      cancelEdit();
    }
  };

  const [departureLoading, setDepartureLoading] = useState(false);
  const departureRequest = useRef(null);
  const departureChosen = useMemo(() => {
    const chosen = new Set(selectedRows);
    return records.filter(r => chosen.has(toId(r.jobid)) && departureSection(r));
  }, [records, selectedRows]);
  const departureAutoCount = departureChosen.filter(r => departureSession?.autoIds.includes(toId(r.jobid))).length;
  const departureManualCount = departureChosen.length - departureAutoCount;
  const departureError = err => {
    let message = err?.message || "No se pudo preparar DEPARTURE.";
    try { const body = err?.response?.data; message = (typeof body === "string" ? JSON.parse(body) : body)?.message || message; } catch {}
    pushToast(message, "danger");
    alert(message);
  };
  const downloadDeparture = async () => {
    if (departureLoading) return;
    setDepartureHint(false);
    setDepartureLoading(true);
    try {
      const res = await Axios.get("/departure_report.php", {
        params: { users: JSON.stringify(userOptions), _: Date.now() }, responseType: "json"
      });
      const data = res.data;
      const wasReviewing = Boolean(departureSession);
      if (data?.status !== "success") throw new Error(data?.message || "No se pudo detectar DEPARTURE.");
      if (departureSession && departureSession.start !== data.start) {
        throw new Error("Cambió la jornada a las 4 AM. Pulsa Nueva detección para comenzar con la jornada actual.");
      }
      const available = data.available_rows || [];
      const eligible = new Set(available.filter(departureSection).map(r => toId(r.jobid)));
      const autoIds = departureSession?.autoIds || [...data.rows].sort((a,b) =>
        String(a.jobuser || "").localeCompare(String(b.jobuser || ""))
        || String(a.jobti || "").localeCompare(String(b.jobti || ""))
        || String(a.jobid).localeCompare(String(b.jobid))
      ).map(r => toId(r.jobid));
      if (!departureSession) {
        clearFilters();
        setShowArchived(true);
        setSortKey(null);
        setSelectedRows(autoIds);
      } else {
        // Preserve manual additions AND unchecked automatic rows on every reopening.
        setSelectedRows(prev => prev.filter(id => eligible.has(id)));
      }
      setRecords(available);
      setDepartureSession({ ...data, autoIds, orderIds: departureSession?.orderIds || autoIds });
      // First click prepares the grid. Only a later click requests confirmation.
      setDepartureDialog(wasReviewing);
      setDepartureHint(!wasReviewing);
      if (!wasReviewing) window.requestAnimationFrame?.(() => {
        if (scrollRef.current) { scrollRef.current.scrollTop = 0; scrollRef.current.scrollLeft = 0; }
      });
    } catch (err) {
      departureError(err);
    } finally {
      setDepartureLoading(false);
    }
  };
  const executeDeparture = async () => {
    if (!departureSession || departureLoading) return;
    setDepartureLoading(true);
    try {
      const selected = departureChosen.map(r => toId(r.jobid));
      const fingerprint = JSON.stringify([departureSession.start, [...selected].sort()]);
      if (departureRequest.current?.fingerprint !== fingerprint) {
        const random = new Uint32Array(4);
        window.crypto.getRandomValues(random);
        departureRequest.current = { fingerprint, id: [...random].map(n => n.toString(16).padStart(8, "0")).join("") };
      }
      const res = await Axios.post("/departure_report.php", {
        request_id: departureRequest.current.id,
        users: userOptions, start: departureSession.start,
        selected
      });
      const data = res.data;
      if (data?.status !== "success") throw new Error(data?.message || "No se pudo generar DEPARTURE.");
      if (data.excluded?.length) {
        setRecords(data.available_rows || []);
        const valid = new Set(data.rows.map(r => toId(r.jobid)));
        setSelectedRows(prev => prev.filter(id => valid.has(id)));
        throw new Error("Estos jobs cambiaron de estado o ya no existen y se desmarcaron: " + data.excluded.join(", ") + ". Revisa la selección y vuelve a Ejecutar.");
      }
      const { text: txt, filename } = formatDepartureReport(data);
      const blobUrl = URL.createObjectURL(new Blob([txt], { type: "text/plain;charset=utf-8" }));
      const link = document.createElement("a");
      link.href = blobUrl; link.download = filename;
      document.body.appendChild(link); link.click(); link.remove();
      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
      setDepartureDialog(false);
      departureRequest.current = null;
      // Clear only after a successful generation and download request.
      setSelectedRows([]);
      setDepartureSession(null);
      setDepartureHint(false);
    } catch (err) { departureError(err); }
    finally { setDepartureLoading(false); }
  };

  const createNewLocalRecord = () => {
    if (isCreating) return;
    setIsCreating(true);
    try {
      const tempId = nextTempId();
      const limaNow = nowSqlLima();

      const newRecord = {
        jobid: tempId,
        jobsta: "",
        jobuser: "",
        jobti: "",
        jobclo: "Opened EP",
        jobclodate: limaNow,
        jobdatesub: limaNow,
        jobpro: "",
        jobnotesadmin: "",
        jobnotes: "",
        jobproname: "",
        jobproport: "",
        jobou: "",
        joblob: "",
        jobcomty: "",
        jobrouteaerial: "",
        jobrouteug: "",
        jobcoaxae: "",
        jobcoaxug: "",
        jobasbae: "",
        jobasbug: "",
        joblode: "",
        jobhc: "",
        jbhrex: "0",
      };

      setRecords((prev) => [newRecord, ...prev]);
      // ← evita que algún orden activo mueva la nueva fila
      pushToast("Fila temporal agregada (arriba).", "success");
    } finally {
      setIsCreating(false);
    }
  };


  async function deleteProjectIds(ids) {
    const realIds = ids
      .filter((id) => id && !isTempId(id))
      .map((id) =>
        String(id)
          .replace(/[\u00A0\u2000-\u200B\u202F\u205F\u3000\t\r\n]/g, " ")
          .replace(/\s+/g, " ")
          .trim()
      );
    const tempIds = ids.filter((id) => !id || isTempId(id));

    if (tempIds.length) {
      setRecords((curr) => curr.filter((r) => !tempIds.includes(String(r.jobid))));
    }
    if (!realIds.length) return { deleted: 0, deletedIds: [] };

    const res = await Axios.post("/delete_project_debug.php", { jobids: realIds }, {
  headers: { "Content-Type": "application/json" }
});
    const deleted = Number(res?.data?.deleted || 0);
    const deletedIds = Array.isArray(res?.data?.deleted_ids)
      ? res.data.deleted_ids.map(String)
      : [];

    if (deletedIds.length) {
      setRecords((curr) => curr.filter((r) => !deletedIds.includes(String(r.jobid))));
    }
    await fetchRecords();
    return { deleted, deletedIds };
  }

  function statusPillClass(val) {
    const s = normalize(val);
    switch (s) {
      case "inprocess": return "pill pill-status-yellow";         // In Process -> amarillo pastel
      case "inhold":    // "In Hold" normalizado
      case "hold":      return "pill pill-status-red";            // Hold -> rojo pastel
      case "readyforqc": return "pill pill-status-green";         // Ready for QC -> verde actual
      case "jobready":  return "pill pill-status-lilac";        // Job Ready -> lila pastel
      case "assigned":   return "pill pill-status-emerald";       // Assigned -> esmeralda
      case "notassigned": return "pill pill-status-white";        // Not Assigned -> blanco
      case "correction": return "pill pill-status-red";        // Correction -> rojo pastel
      case "solvingcorrection": return "pill pill-status-fuchsia";// Solving Correction -> fucsia pastel
      case "archived": return "pill pill-status-gris";// Solving Correction -> fucsia pastel
      default: return "pill pill-status-green";
    }
  }
  function typePillClass(val) {
    const s = normalize(val);
    switch (s) {
      case "coaxasbuilt": return "pill pill-type-coax-asbuilt";
      case "coaxasbrevertsplit":
      case "coaxasbrevertn+0":
      case "coaxasbrevertn0":
      case "coaxasbrevert":
      case "coaxasbuiltrevert": return "pill pill-type-coax-asbuilt-revert";
      case "coaxdesign": return "pill pill-type-coax-design";
      case "coaxbentley": return "pill pill-type-coax-bentley";
      case "coaxbentleyasb":
      case "coaxbentleydes": return "pill pill-type-coax-bentley-asb";
      case "route": return "pill pill-type-route";
      case "routefttp": return "pill pill-type-route-fttp";
      case "fiberfttp": return "pill pill-type-fiber-fttp";
      case "fiberdesignbau": return "pill pill-type-fiber-design-bau";
      case "fiberasbuiltbau":
      case "fiberbentleyasb":
      case "fiberbentleydes": return "pill pill-type-fiber-asbuilt-bau";
      case "fiberiris":
      case "irissplitfiberdesign":
      case "irissplitfiberasbuilt":
      case "bentleysplitfiberdesign":
      case "bentleysplitfiberasbuilt": return "pill pill-type-fiber-iris";
      case "irissplits":
      case "irissplitcoaxdesign":
      case "irissplitcoaxasbuilt":
      case "bentleysplitcoaxdesign":
      case "bentleysplitcoaxasbuilt": return "pill pill-type-coax-bentley";
      case "othertasks":
      case "tasks": return "pill pill-type-tasks";
      default: return "pill pill-type";
    }
  }
function epPillClass(val){
  const s = normalize(val);
  if (s === "openedep") return "pill pill-openep";
  if (s === "hotjob" || s === "hotpriority") return "pill pill-hotjob";
  return "pill pill-closeep";
}


  const projectPalette = {
    "project01": { bg: "#fcf5c7", fg: "#5D2A0A" },
    "project02": { bg: "#F4C6BC", fg: "#5D1A0A" },
    "project03": { bg: "#ddedea", fg: "#4A2A6A" },
    "project04": { bg: "rgb(248, 182, 248)", fg: "#4A2A6A" },
    "project05": { bg: "#DDF4ED", fg: "#0B4F4A" },
    "project06": { bg: "#daeaf6", fg: "#104A7A" },
    "project07": { bg: "#EAF5D7", fg: "#3D5E0A" },
    "project08": { bg: "#efe8c9", fg: "#5D4B00" },
  };
  const projectStyle = (val) => {
    const k = normalize(val);
    const c = projectPalette[k];
    return c ? { background: c.bg, color: c.fg } : {};
  };
  const optionClassForProject = (val) =>
    "project-opt-" + normalize(val).replace(/[\s-]+/g, "");

  /* ============== Export XLS ============== */
  const exportToXLS = () => {
    const rows = filteredRecords || [];
    const thStyle =
      "background:#efe7e3;color:#2f2b26;font-weight:700;text-align:center;white-space:nowrap;border:1px solid #ddd;padding:8px 6px;";
    const tdStyle =
      "border:1px solid #ddd;padding:6px 6px;text-align:center;white-space:nowrap;font-size:12px;";
    const tdLeft = tdStyle + "text-align:left;";
    const pillBase =
      "display:inline-block;padding:6px 12px;border-radius:8px;font-size:12px;font-weight:700;border:1px solid rgba(0,0,0,.06);";
    const statusGreen = "background:#E6F4EA;color:#195A2B;";

    const htmlHead = `
<meta charset="utf-8" />
<style>
  table{ border-collapse:collapse; font-family:Arial,Helvetica,sans-serif; }
  thead tr th{ ${thStyle} }
  tbody tr td{ ${tdStyle} }
</style>`.trim();

    const headers = [
      "#",
      "Fav",
      "Job Id",
      "Status",
      "User",
      "Type",
      "EP Status",
      "Created at",
      "Project",
      "Submission Date",
      "Notes Admin",
      "Notes",
      "OU-Comcast",
      "Route Aerial",
      "Route UG",
      "Coax AE",
      "Coax UG",
      "Asb AE",
      "Asb UG",
      "Lode / Nodes",
      "HC / Amps",
      "Hr Extras",
      "Project Name",
      "Project Port",
      "COM Job Type",
      "LOB"
    ];

    const thead =
      "<thead><tr>" +
      headers.map((h) => `<th style="${thStyle}">${escapeHtml(h)}</th>`).join("") +
      "</tr></thead>";

    const tbody =
      "<tbody>" +
      rows
        .map((r, idx) => {
          const id = toId(r.jobid);
          const isFav = favoriteRows.includes(id);
          const proj = projectStyle(r.jobpro);
          const projStyle = `background:${proj.background || proj.bg || "#f6d9cf"};color:${proj.color || proj.fg || "#5d1a0a"};`;

          return (
            "<tr>" +
            `<td style="${tdLeft}">${escapeHtml(r.jobid || "")}</td>` +
            `<td style="${tdLeft}"><span style="${pillBase}${statusGreen}">${escapeHtml(r.jobsta || "")}</span></td>` +
            `<td style="${tdLeft}"><span style="${pillBase}${projStyle}">${escapeHtml(r.jobpro || "")}</span></td>` +
            `<td style="${tdStyle}">${idx + 1}</td>` +
            `<td style="${tdStyle}">${isFav ? "★" : ""}</td>` +
            `<td style="${tdLeft}">${escapeHtml(r.jobuser || "")}</td>` +
            `<td style="${tdLeft}">${escapeHtml(r.jobti || "")}</td>` +
            `<td style="${tdLeft}">${escapeHtml(r.jobclo || "")}</td>` + `
            <td style="${tdStyle}">${escapeHtml(formatDateTimeHMLima(r.jobcreatedat || ""))}</td>` +
            `<td style="${tdStyle}">${escapeHtml(formatDateTimeHMLima(r.jobdatesub || ""))}</td>` +
            `<td style="${tdLeft}">${escapeHtml(r.jobnotesadmin || "")}</td>` +
            `<td style="${tdLeft}">${escapeHtml(r.jobnotes || "")}</td>` +
            `<td style="${tdStyle}">${escapeHtml(r.jobcomty || "")}</td>` +
            `<td style="${tdStyle}">${escapeHtml(r.jobrouteaerial ?? "")}</td>` +
            `<td style="${tdStyle}">${escapeHtml(r.jobrouteug ?? "")}</td>` +
            `<td style="${tdStyle}">${(parseFloat(r.jobrouteaerial)||0) + (parseFloat(r.jobrouteug)||0)}</td>` +
            `<td style="${tdStyle}">${escapeHtml(r.jobcoaxae ?? "")}</td>` +
            `<td style="${tdStyle}">${escapeHtml(r.jobcoaxug ?? "")}</td>` +
            `<td style="${tdStyle}">${(parseFloat(r.jobcoaxae)||0) + (parseFloat(r.jobcoaxug)||0)}</td>` +
            `<td style="${tdStyle}">${escapeHtml(r.jobasbae ?? "")}</td>` +
            `<td style="${tdStyle}">${escapeHtml(r.jobasbug ?? "")}</td>` +
            `<td style="${tdStyle}">${(parseFloat(r.jobasbae)||0) + (parseFloat(r.jobasbug)||0)}</td>` +
            `<td style="${tdStyle}">${escapeHtml(r.joblode ?? "")}</td>` +
            `<td style="${tdStyle}">${escapeHtml(r.jobhc ?? "")}</td>` +
            `<td style="${tdStyle}">${escapeHtml(r.jbhrex ?? "")}</td>` +
            `<td style="${tdStyle}">${escapeHtml(r.jobproname || "")}</td>` +
            `<td style="${tdStyle}">${escapeHtml(r.jobproport || "")}</td>` +
            `<td style="${tdStyle}">${escapeHtml(r.jobou || "")}</td>` +
            `<td style="${tdStyle}">${escapeHtml(r.joblob || "")}</td>` +
            "</tr>"
          );
        })
        .join("") +
      "</tbody>";

    const html = `
<!DOCTYPE html>
<html>
<head>${htmlHead}</head>
<body>
  <table>${thead}${tbody}</table>
</body>
</html>`.trim();

    const blob = new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const ts = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const name = `projects_${ts.getFullYear()}${pad(ts.getMonth() + 1)}${pad(ts.getDate())}_${pad(
      ts.getHours()
    )}${pad(ts.getMinutes())}.xls`;
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
  document.body.removeChild(a);
URL.revokeObjectURL(url);

  };

  // A small horizontal movement makes the frozen columns visible on activation.
  useEffect(() => {
    if (!freezeColumns) return;
    const frame = requestAnimationFrame(() => {
      scrollRef.current?.scrollBy({ left: 40, top: 0, behavior: "smooth" });
    });
    return () => cancelAnimationFrame(frame);
  }, [freezeColumns]);

  // Measure actual column widths so frozen offsets remain aligned after resizing.
  useEffect(() => {
    if (!freezeColumns) return;
    const scroller = scrollRef.current;
    const table = scroller?.querySelector("table.grid-table");
    const cells = table?.tHead?.rows[0]?.cells;
    if (!cells || cells.length < 3) return;
    const updateFrozenOffsets = () => {
      const selectionWidth = cells[0].getBoundingClientRect().width;
      const starWidth = cells[1].getBoundingClientRect().width;
      table.style.setProperty("--frozen-star-left", `${selectionWidth}px`);
      table.style.setProperty("--frozen-job-left", `${selectionWidth + starWidth}px`);
    };
    updateFrozenOffsets();
    const observer = new ResizeObserver(updateFrozenOffsets);
    observer.observe(cells[0]);
    observer.observe(cells[1]);
    observer.observe(table);
    return () => observer.disconnect();
  }, [loggedUser, freezeColumns]);

  if (!loggedUser) {
    return (
      <div className="container my-5" style={{ maxWidth: "400px" }}>
        <h3 className="mb-4 text-center">Por favor inicia sesión primero.</h3>
      </div>
    );
  }
if (!loggedUser) return null; // evita parpadeo mientras se valida sesión


  return (
    <div className="container-fluid grid-page" style={{ paddingLeft: 0, paddingRight: 0 }}>
      <style>{`

        /* === QC highlight + post-it === */
        .qc-flash td{ background:#FFF3BF !important; transition: background .3s ease; }
        @keyframes qcPulse { 0%{ box-shadow: inset 0 0 0 0 rgba(255,193,7,.6); } 50%{ box-shadow: inset 0 0 0 9999px rgba(255,193,7,.25);} 100%{ box-shadow: inset 0 0 0 0 rgba(255,193,7,.0);} }
        .qc-flash td{ animation: qcPulse 1.8s ease-in-out 1; }
        .qc-postit{ background:#FFE3D6; color:#5A3E36; border:1px solid #F3C3B2; border-radius:10px; box-shadow:0 12px 28px rgba(0,0,0,.14); }
        .qc-postit::before{ content:""; position:absolute; left:12px; top:10px; width:120px; height:90px; pointer-events:none; background:radial-gradient(ellipse at 30% 20%, rgba(255,255,255,.45), rgba(255,255,255,0) 70%); }
        .qc-postit::after{ content:""; position:absolute; bottom:-6px; left:8px; width:64px; height:22px; pointer-events:none; background:radial-gradient(ellipse at top, rgba(0,0,0,.18), rgba(0,0,0,0) 70%); transform:skewX(-12deg); filter:blur(.4px); opacity:.35; }
        :root{
          --grid-card-bg: #D5DBE2;
          --th-color: #2f2b26;
  --wide-col-w: 350px;

          /* Anchos independientes de las cuatro últimas columnas */
          --project-name-w: 400px;
          --project-port-w: 100px;
          --com-job-type-w: 100px;
          --lob-w: 150px;

          /* selección de fila */
          --row-selected-bg: #eaf6ee;
          --row-selected-bg-hover: #e2f3e7;
        }

        
        /* ======= Hero superior ======= */
        .hero-wrap{
          position: relative;
          width: 100%;
          height: clamp(112px, 22.4vh, 160px);
          border-radius: 12px;
          overflow: hidden;
          margin-bottom: 4px;
          background: #E5E9EE;
        }
        .hero-img{
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: 50% 52%;
          display: block;
        }
        .hero-wrap::after{
          content:"";
          position:absolute; inset:0;
          background: linear-gradient(90deg, rgba(16,23,32,.78) 0%, rgba(16,23,32,.52) 22%, rgba(16,23,32,.18) 50%, rgba(16,23,32,0) 82%);
          pointer-events:none;
        }
        .brand-lockup{
          position:absolute; left:22px; top:12px; z-index:2;
          display:flex; align-items:center; gap:8px;
          width:min(334px,44vw); height:80px;
          transform:scale(.64); transform-origin:top left;
          filter:drop-shadow(0 2px 4px rgba(0,0,0,.28));
        }
        .brand-emblem{ width:64px; height:76px; flex:0 1 64px; min-width:0; }
        .brand-wordmark{ width:260px; height:78px; flex:1 1 260px; min-width:0; }

        .flowing-brand{
          position:absolute; right:26px; top:22px; z-index:2;
          display:flex; align-items:center; gap:12px;
          color:#F7F9FC; font-family:"Segoe UI",system-ui,sans-serif;
          font-size:26px; font-weight:500;
          letter-spacing:-.5px; line-height:1.2;
          filter:drop-shadow(3px 4px 5px rgba(0,0,0,.85));
        }
        .flowing-mark{
          display:grid; grid-template-columns:8px 8px;
          grid-template-rows:8px 8px; gap:5px;
        }
        .flowing-mark i{ display:block; border-radius:2px; background:currentColor; }
        .flowing-mark i:first-child{ grid-column:2; }


        .grid-card{
          display:flex; flex-direction:column;
          background: var(--grid-card-bg) !important;
          border-radius: 12px; min-height:0;
          position: relative;
        }
        .grid-scroll{
          position:relative; width:100%;
          overflow:auto;
          max-height: calc(100vh - 250px);
        }
        table.grid-table{
          width:100%;
          min-width:1800px;
          text-align:center;
          vertical-align:middle;
          table-layout:fixed;
        }
        table.grid-table thead th{
  position: sticky; top: 0;
  z-index: 3;
  background: #E5E9EE;
  color: var(--th-color);
  font-weight: 700;
  vertical-align: middle;
  white-space: nowrap;
}
/* === Header controls: keep everything ACTIVE but match header background (no white) === */
table.grid-table thead th,
table.grid-table thead td{
  background: #E5E9EE !important;
}
table.grid-table thead th .form-control,
table.grid-table thead th .form-select,
table.grid-table thead td .form-control,
table.grid-table thead td .form-select{
  background: #E5E9EE !important;
  border-color: rgba(0,0,0,.12) !important;
  box-shadow: none !important;
}
/* Job Id header: keep pastel yellow even with global header bg */
table.grid-table thead th .form-control.header-jobid-yellow{
  background:#FFF3BF !important;
  border-color:#e6d08a !important;
  color:#111 !important;
}
/* keep yellow even when focused */
table.grid-table thead th .form-control.header-jobid-yellow:focus{
  background:#FFF3BF !important;
  box-shadow: 0 0 0 .2rem rgba(230, 208, 138, .25) !important;
}

table.grid-table thead th .form-control:focus,
table.grid-table thead th .form-select:focus,
table.grid-table thead td .form-control:focus,
table.grid-table thead td .form-select:focus{
  background: #E5E9EE !important;
  box-shadow: 0 0 0 .2rem rgba(90, 62, 54, .12) !important;
}


/* === Dark mode: header controls match dark header bg (no white) === */
body.dark-mode table.grid-table thead th,
body.dark-mode table.grid-table thead td{
  background: #4a4a4a !important;
}
body.dark-mode table.grid-table thead th .form-control,
body.dark-mode table.grid-table thead th .form-select,
body.dark-mode table.grid-table thead td .form-control,
body.dark-mode table.grid-table thead td .form-select,
body.dark-mode .header-status-btn{
  background: #4a4a4a !important;
}
/* Keep Job Id pastel yellow even in dark mode */
body.dark-mode table.grid-table thead th .form-control.header-jobid-yellow{
  background:#FFF3BF !important;
  border-color:#e6d08a !important;
  color:#111 !important;
}
.grid-table .pill-openep { background:#a0ced9 !important; color:#0B4A68 !important; }
.grid-table .pill-closeep{ background:#f7cfd1 !important; color:#5f4b00 !important; }
.grid-table .pill-hotjob{ background:#f7cfd1 !important; color:#6a1e22 !important; border:1px solid #efb2b6 !important; }

        :where(th[data-label="Notes"], th[data-label="OU-Comcast"]){
          width: var(--wide-col-w); min-width: var(--wide-col-w);
        }
        td.wide-col{ width: var(--wide-col-w); min-width: var(--wide-col-w); }

        .grid-page table.grid-table th[data-label="Project Name"],
        .grid-page table.grid-table td.project-name-col{
          width:var(--project-name-w); min-width:var(--project-name-w);
        }
        .grid-page table.grid-table th[data-label="Project Port"],
        .grid-page table.grid-table td.project-port-col{
          width:var(--project-port-w); min-width:var(--project-port-w);
        }
        .grid-page table.grid-table th[data-label="COM-Job type"],
        .grid-page table.grid-table td.com-job-type-col{
          width:var(--com-job-type-w); min-width:var(--com-job-type-w);
        }
        .grid-page table.grid-table th[data-label="LOB"],
        .grid-page table.grid-table td.lob-col{
          width:var(--lob-w); min-width:var(--lob-w);
        }
        table.grid-table{ table-layout: fixed; }
        
        /* sort arrows */
     th.sortable { position: relative; padding-right: 22px; }
.sort-arrow { position: absolute; right: 8px; top: 6px; font-size: 12px; opacity: .8; pointer-events: none; }

td.clip-cell{ overflow:hidden !important; }
        td.clip-cell .clip-flex{ display:flex; min-width:0; }
        td.clip-cell .clip-text{ flex:1 1 auto; min-width:0; overflow:hidden !important; text-overflow:ellipsis !important; white-space:nowrap !important; }
        td.clip-cell .clip-text *{ white-space:nowrap !important; max-width:100%; }
        
          position: sticky; top: 0;
          z-index: 3;
          background: #E5E9EE;
          color: var(--th-color);
          font-weight:700;
          vertical-align: middle;
          white-space: nowrap;
        }

        .pill{
          display:inline-block; padding:6px 12px;
          border-radius: 8px; font-size:13px; line-height:1; font-weight:700;
          border:1px solid rgba(0,0,0,0.06); user-select:none; white-space:nowrap;
        }

        .pill-status-green{ background:#E6F4EA; color:#195A2B; }
        .pill-status-lilac{ background:#EBD6FF; color:#4A2A6A; } /* lila pastel (Job Ready) */
        .pill-status-yellow{ background:#FFF3BF; color:#7A5B00; } /* amarillo pastel */
        .pill-status-red{ background:#FCE1E1; color:#7A1F1F; }     /* rojo pastel */
        .pill-status-salmon{ background:#FFD6CC; color:#7A2E1F; }  /* salmón pastel */
        .pill-status-fuchsia{ background:#FAD0F5; color:#7A1E6B; } /* fucsia pastel */
        .pill-status-emerald{ background:#A7F3D0; color:#065F46; } /* esmeralda */
        .pill-status-white{ background:#FFFFFF; color:#333333; }   /* blanco */

        .pill-status-yellow{ background:#FFF3BF; color:#7A5B00; } /* amarillo pastel */
        .pill-status-red{ background:#FCE1E1; color:#7A1F1F; }     /* rojo pastel */
        .pill-status-salmon{ background:#FFD6CC; color:#7A2E1F; }  /* salmón pastel */
        .pill-status-fuchsia{ background:#FAD0F5; color:#7A1E6B; } /* fucsia pastel */
        .pill-status-gris{ background:#FAD0F5; color:#333333; }   /* gris */
        .pill-user{ background:#ddeda; color:#0b3e91; }
        .pill-type{ background:#f4f0d6; color:#6d5c00; }
        .pill-closeep{ background:#efe8c9; color:#5f4b00; }
        .pill-hotjob{ background:#f7cfd1; color:#6a1e22; border:1px solid #efb2b6; }
.pill-project{ background:#f6d9cf; color:#5d1a0a; }
        /* === Type pastel colors === */
        .pill-type-coax-asbuilt{ background:#EBD6FF; color:#4A2A6A; }
        .pill-type-coax-asbuilt-revert{ background:#EBD6FF; color:#4A2A6A; }
        .pill-type-coax-design{ background:#a18262; color:#5D2A0A; }
        .pill-type-coax-bentley{ background:#cca9dd; color:#104A7A; }
        .pill-type-route{ background:#EAF5D7; color:#3D5E0A; }
        .pill-type-route-fttp{ background:#DDF4ED; color:#0B4F4A; }
        .pill-type-fiber-fttp{ background:#D9F2FF; color:#0B4A68; }
        .pill-type-fiber-design-bau{ background:#F4F0D6; color:#6D5C00; }
        .pill-type-fiber-asbuilt-bau{ background:#F2E5FF; color:#4A2A6A; }
        .pill-type-fiber-asbuilt-revert{ background:#FAD0F5; color:#7A1E6B; }
        .pill-type-fiber-correction-to-pay{ background:#FAD0F5; color:#7A1E6B; }
        .pill-type-fiber-bentley{ background:#E6EFFF; color:#0B3E91; }
        .pill-type-fiber-iris{ background:#cfefff; color:#0b5f7a; border:1px solid #a9dff5; }
        .pill-type-tasks{ background:#EDEFF5; color:#2F3A5B; }


        .cell-editable{ cursor:pointer; }
        .cell-editable:hover{ background:#fafafa; }
        .grid-table td{ padding: 8px 8px; }
        .grid-table th{ padding: 5.4px 8px; }

        .filter-cell{ position:relative; }
        .filter-cell select{ width:100%; }
        .filter-cell.show-placeholder:not(:hover):not(:focus-within) select{
          color: transparent; -webkit-text-fill-color: transparent;
        }
        .filter-cell.show-placeholder:not(:hover):not(:focus-within)::after{
          content: attr(data-label);
          position:absolute; left:12px; top:50%; transform: translateY(-50%);
          pointer-events:none; color: var(--th-color); font-weight:700;
        }
        /* Center User, Type, EP Status and Project labels and filter values. */
        .grid-table th.filter-cell.show-placeholder::after{
          left:0; right:0; text-align:center;
        }
        .grid-table th.filter-cell.show-placeholder .header-select{
          text-align:center; text-align-last:center;
          padding-left:8px; padding-right:8px;
        }
        .filter-cell.show-placeholder:hover::after,
        .filter-cell.show-placeholder:focus-within::after{
          content: "";
        }
        .filter-cell select option{ color:#212529; }

        /* ===== Encabezados de INPUT como los selects (label en reposo) ===== */
        .filter-input { position: relative; }
        .filter-input:has(.header-input:placeholder-shown):not(:hover):not(:focus-within)::after{
          content: attr(data-label);
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          pointer-events: none;
          color: var(--th-color);
          font-weight: 700;
        }

        /* FIX: Job Id label gris en modo dark (fondo amarillo) */
        body.dark-mode .filter-input:has(.header-jobid-yellow.header-input:placeholder-shown):not(:hover):not(:focus-within)::after{
          color:#555 !important;
        }
        .filter-input:has(.header-input:placeholder-shown):not(:hover):not(:focus-within) .header-input{
          color: transparent;
          -webkit-text-fill-color: transparent;
          caret-color: transparent;
        }
        .filter-input:hover::after,
        .filter-input:focus-within::after{ content: ""; }

        .left-cell{ text-align:left; }

        /* checkbox/índice */
        td.sel-cell{ position:relative; }
        .row-index{ display:inline-block; min-width: 20px; opacity: 0.9; }
        .row-check{
          position:absolute; left:50%; top:50%; transform: translate(-50%, -50%);
          opacity:0; pointer-events:none; transition: opacity .15s ease;
        }
        td.sel-cell:hover .row-check{ opacity:1; pointer-events:auto; }
        td.sel-cell:hover .row-index{ opacity:0; }

        /* Estrella favorito */
        .fav-star{
          cursor:pointer;
          font-size:20px;
          line-height:1;
          color:#ff9800;
          transition: transform .12s ease, opacity .12s ease;
        }
        .fav-star.off{ opacity:.85; }
        .fav-star:hover{ transform: scale(1.1); }

        /* ===== Botón + Add naranja ===== */
        .btn-add{
          background:#ff7a00;
          border-color:#ff7a00;
          color:#fff;
        }
        .btn-add:hover{ filter: brightness(.92); }
        .btn-add:disabled{ opacity:.7; }

        /* Celdas vacías (visual) */
        .cell-empty{ color:#9aa0a6; font-style: italic; }

        /* ====== Fila seleccionada ====== */
        .grid-table tbody tr.row-selected td{
          background: var(--row-selected-bg) !important;
        }
        .grid-table tbody tr.row-selected:hover td{
          background: var(--row-selected-bg-hover) !important;
        }
        .grid-table tbody tr.row-selected td.sel-cell .row-check{
          opacity: 1;
          pointer-events: auto;
        }
        .grid-table tbody tr.row-selected td.sel-cell .row-index{
          opacity: 0;
        }

        /* === Clamp exclusivo para Notes (evita que "chancE" columnas) === */
        .notes-clip{
          display:block;
          overflow:hidden;
          text-overflow:ellipsis;
          white-space:nowrap;
        }      
/* ======= Topbar buttons (pastel) ======= */
.grid-topbar{ --toolbar-button-height:35px; }
.grid-topbar .btn{ font-size:14px; font-weight:500; padding:6px 12px; border-width:1px; }
/* Same height for toolbar actions, including the FYI launcher. */
.grid-topbar .btn,
.grid-topbar .fyi-launch{
  height:var(--toolbar-button-height);
  min-height:var(--toolbar-button-height);
  box-sizing:border-box;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  line-height:21px;
}
.grid-topbar .fyi-launch{ padding-top:0; padding-bottom:0; }
.grid-topbar .departure-envelope-wrap{ display:inline-flex; align-items:center; }
.btn-pastel-blue{ background:#D6F3FF; border-color:#6EC6FF; color:#0B6AA6; }
.btn-pastel-blue:hover{ filter:brightness(.96); }
.btn-pastel-green{ background:#8FBC8F; border-color:#C8E6C9; color:#2E7D32; }
.btn-pastel-green:hover{ filter:brightness(.96); }
.btn-pastel-gray{ background:#696969; border-color:#BDBDBD; color:#FFFFFF; }
.btn-pastel-gray:hover{ filter:brightness(.96); }
.btn-pastel-purple{ background:#696969; border-color:#D1C4E9; color:#F8F8FF; }
.btn-pastel-purple:hover{ filter:brightness(.96); }
.btn-pastel-peach{ background:#FFE7E1; border-color:#FFC2B3; color:#B24C2E; }
.btn-pastel-peach:hover{ filter:brightness(.96); }
.btn-solid-danger{ background:#E53935; border-color:#E53935; color:#fff; }
.btn-solid-danger:hover{ filter:brightness(.95); }


/* Force pastel colors even when disabled (Bootstrap override) */
.grid-topbar .btn-pastel-peach:disabled,
.grid-topbar .btn-pastel-peach[disabled]{
  background:#FFE7E1 !important;
  border-color:#FFC2B3 !important;
  color:#B24C2E !important;
  opacity:.85;
}
.grid-topbar .btn-pastel-red:disabled,
.grid-topbar .btn-pastel-red[disabled]{
  background:#FFE5E5 !important;
  border-color:#FFB3B3 !important;
  color:#C62828 !important;
  opacity:.85;
}
.btn-pastel-peach:disabled, .btn-pastel-red:disabled { opacity:.65; filter:none; }
.btn-pastel-red{ background:#FFE5E5; border-color:#FFB3B3; color:#C62828; }
.btn-pastel-red:hover{ filter:brightness(.96); }


  /* === Restore pill visuals (shape + colors) === */
  .grid-table .pill{
    display:inline-flex; align-items:center; justify-content:center;
    height:26px; padding:4px !important; border-radius:4px !important;
    line-height:1; box-sizing:border-box; border:1px solid rgba(0,0,0,.06);
  }

  
  /* Status */
  .grid-table .pill-status-green{ background:#BFCDC0 !important; color:#195A2B !important; }
  .grid-table .pill-status-lilac{ background:#EBD6FF !important; color:#4A2A6A !important; }
  .grid-table .pill-status-yellow{ background:#efe8c9 !important; color:#7A5B00 !important; }
  .grid-table .pill-status-red{ background:#FCE1E1 !important; color:#7A1F1F !important !important; }
  .grid-table .pill-status-salmon{ background:#FFD6CC !important; color:#7A2E1F !important; }
  .grid-table .pill-status-fuchsia{ background:#FAD0F5 !important; color:#7A1E6B !important; }
  .grid-table .pill-status-emerald{ background:#F7D1B9 !important; color:#065F46 !important; }
  .grid-table .pill-status-white{ background:#FFFFFF !important; color:#333333 !important; }




  
  /* User */
  .grid-table .pill-user{ background:#d6eadf !important; color:#051020 !important; }
  /* Type (generic + specifics) */
  .grid-table .pill-type{ background:#efe8c9 !important; color:#6d5c00 !important; }
  .grid-table .pill-type-coax-asbuilt{ background:#eac4d5 !important; color:#4A2A6A !important; }
  .grid-table .pill-type-coax-asbuilt-revert{ background:#EBD6FF !important; color:#4A2A6A !important; }
  .grid-table .pill-type-coax-design{ background:#F7D1B9 !important; color:#5D2A0A !important; }
  .grid-table .pill-type-coax-bentley{ background:#B3EBF2 !important; color:#0a0a0a !important; }
  .grid-table .pill-type-coax-bentley-asb{ background:#D4F0F0 !important; color:#224652 !important; }
  .grid-table .pill-type-route{ background:#EAF5D7 !important; color:#3D5E0A !important; }
  .grid-table .pill-type-route-fttp{ background:#DDF4ED !important; color:#0B4F4A !important; }
  .grid-table .pill-type-fiber-fttp{ background:#D9F2FF !important; color:#0B4A68 !important; }
  .grid-table .pill-type-fiber-design-bau{ background:#F4F0D6 !important; color:#0a0a0a !important; }
  .grid-table .pill-type-fiber-asbuilt-bau{ background:#F2E5FF !important; color:#4A2A6A !important; }
  .grid-table .pill-type-fiber-asbuilt-revert{ background:#FAD0F5 !important; color:#7A1E6B !important; }
  .grid-table .pill-type-fiber-correction-to-pay{ background:#FF746C !important; color:#7A1E6B !important; }
  .grid-table .pill-type-fiber-bentley{ background:#b6afac !important; color:#0a0a0a !important; }
  .grid-table .pill-type-fiber-iris{ background:#cfefff !important; color:#0b5f7a !important; border:1px solid #a9dff5 !important; }
  .grid-table .pill-type-tasks{ background:#EDEFF5 !important; color:#2F3A5B !important; }
  /* EP */
  .grid-table .pill-openep{ background:#efe8c9 !important; color:#5f4b00 !important; }
  .grid-table .pill-closeep{ background:#d4c6aa !important; color:#5f4b00 !important; }
  .grid-table .pill-hotjob{ background:#f7cfd1 !important; color:#6a1e22 !important; border:1px solid #efb2b6 !important; }
/* Project (colors van por projectStyle; pill solo hereda) */
  .grid-table .pill-project{ background: transparent; color: inherit; }
  /* Status/User widths iguales (opcional) */
  .grid-table td.status-cell .pill,
  .grid-table td.user-cell .pill{ width:156px !important; min-width:115px !important; max-width:115px !important; }
  /* Celda Notes cuando ⭐ */
  .grid-table td.notes-starred{ background:#FFF3BF !important; }

/* === Viewport-fixed height for grid with vertical scroll === */
  :root{ --grid-viewport-offset: calc(clamp(112px, 22.4vh, 160px) + 88px); }
  .grid-card{ display:flex; flex-direction:column; }
  .grid-scroll{ flex:1 1 auto; min-height:0; overflow-y:auto; max-height: calc(100dvh - var(--grid-viewport-offset)); overscroll-behavior: contain; }
  

        /* === Dark mode (grises, sin azules) === */

        /* === Encabezados / filtros (solo estilo) === */
        .header-select, .header-input, .header-status-btn{
          border-radius: 8px;
        }
        /* Default header control bg (avoid white) */
        .header-input, .header-select, .header-status-btn{ background:#E5E9EE; }
        body.dark-mode .header-input, body.dark-mode .header-select, body.dark-mode .header-status-btn{ background:#424242; }

        .header-filter-active{
          background:#D6F3FF !important;
          color:#111 !important;
          border-color:#9dd9ef !important;
        }
        .header-filter-active::placeholder{ color:#333 !important; }
        body.dark-mode .header-filter-active{
          background:#D6F3FF !important;
          color:#111 !important;
        }
        body.dark-mode .header-filter-active::placeholder{ color:#333 !important; }

        /* Job Id siempre amarillo */
        .header-jobid-yellow{
          background:#FFF3BF !important;
          color:#111 !important;
          border-color:#e6d08a !important;
        }
        .header-jobid-yellow::placeholder{ color:#333 !important; }
        body.dark-mode .header-jobid-yellow{
          background:#FFF3BF !important;
          color:#111 !important;
        }
        body.dark-mode .header-jobid-yellow::placeholder{ color:#333 !important; }

        /* Dropdown options legibles en dark en encabezados */
        body.dark-mode .table thead .header-select option{
          background:#ffffff !important;
          color:#111 !important;
        }
        body.dark-mode { background:#3a3a3a; color:#efefef; 
  --th-color: #f0f0f0;
}
        body.dark-mode .card, body.dark-mode .modal-content, body.dark-mode .dropdown-menu { background:#3b3b3b; color:#efefef; border-color:#5a5a5a; }
        body.dark-mode .table { color:#e6e6e6; }
        body.dark-mode .table thead th { background:#4a4a4a; color:#f2f2f2; border-color:#666; }
        body.dark-mode .table tbody tr { background:#3b3b3b; border-color:#555; }
        body.dark-mode .form-control, body.dark-mode input, body.dark-mode textarea, body.dark-mode select { background:#424242; color:#fafafa; border-color:#666; }
        body.dark-mode .form-check-label, body.dark-mode label { color:#e6e6e6; }
        body.dark-mode .topbar { background:#424242; }
        /* No tocamos pastillas/colores pastel existentes */
    
        body.dark-mode .table { background:#3a3a3a; }

        body.dark-mode{
          --grid-card-bg: #3b3b3b;
          --row-selected-bg: #4a524d;
        }
        
        .departure-review-active .row-check{ opacity:1!important; pointer-events:auto!important; }
        .departure-review-active .row-index{ opacity:0!important; }
        .departure-review-active .grid-table td.sel-cell{ overflow:visible; }
        .departure-dialog-overlay{ position:fixed; inset:0; z-index:11000; background:rgba(0,0,0,.5); display:flex; align-items:center; justify-content:center; padding:16px; }
        .departure-dialog{ width:min(580px,100%); max-height:88vh; overflow:auto; background:#f4efeb; color:#292623; border:1px solid #bbaa9c; border-radius:12px; padding:20px; }
        body.dark-mode .departure-dialog{ background:#303030; color:#eee; border-color:#666; }
        .grid-topbar{ gap:4px!important; align-items:center; }
        .grid-topbar .btn{ padding-left:10px; padding-right:10px; }
        .departure-envelope-wrap{ position:relative; display:inline-flex; }
        .departure-help{ position:absolute; top:calc(100% + 10px); left:50%; transform:translateX(-50%); width:300px; z-index:10500; padding:12px 30px 12px 12px; border-radius:8px; background:#edf5ed; border:1px solid #8FBC8F; color:#285a2a; box-shadow:0 5px 18px #0002; font-size:13px; line-height:1.45; }
        .departure-help::before{ content:""; position:absolute; top:-7px; left:calc(50% - 7px); width:12px; height:12px; background:inherit; border-top:1px solid #8FBC8F; border-left:1px solid #8FBC8F; transform:rotate(45deg); }
        .departure-help-close{ position:absolute; right:7px; top:5px; border:0; background:transparent; color:inherit; font-size:20px; cursor:pointer; }
        body.dark-mode .departure-help{ background:#26382a; color:#d8edda; }
        .grid-topbar .departure-download{ width:34px; min-width:34px; padding:6px!important; }
        .moon-toggle{ cursor:pointer; padding:6px; border-radius:8px; display:inline-flex; align-items:center; user-select:none; color:#e6c34a; }
        .moon-toggle:hover{ background:rgba(0,0,0,.06); }
        body.dark-mode .moon-toggle:hover{ background:rgba(255,255,255,.06); }
        
        /* Dark mode: grid/table surfaces */
        body.dark-mode .grid-card { background:#3b3b3b !important; }
        body.dark-mode .grid-scroll { background:#3a3a3a; }
        body.dark-mode .grid-table { background:#3a3a3a; }
        body.dark-mode .grid-table th,
        body.dark-mode .grid-table td { background:#3b3b3b !important; color:#efefef; border-color:#5a5a5a; }
        body.dark-mode .table-bordered>:not(caption)>*{ border-width:1px 0; }
        body.dark-mode .table-bordered>:not(caption)>* > *{ border-color:#5a5a5a; }
        body.dark-mode .table-hover tbody tr:hover { background:#4a4a4a !important; }

        /* Dark mode: light text for titles and labels */
        body.dark-mode .grid-card h5 { color:#f3f3f3 !important; }
        body.dark-mode .grid-topbar,
        body.dark-mode .grid-topbar .form-check-label,
        body.dark-mode .grid-topbar label { color:#f0f0f0 !important; }
    
        /* Fill the viewport even when filtering leaves only a few records. */
        .grid-page{
          position:fixed; inset:0;
          width:100%; height:100%;
          margin:0 !important;
          display:flex; flex-direction:column;
          overflow:hidden; box-sizing:border-box;
        }
        .grid-page > .hero-wrap{ flex-shrink:0; }
        .grid-page > .grid-card{ flex:1 1 0; min-height:0; }
        .grid-page .grid-scroll{ flex:1 1 0; min-height:0; max-height:none; }
        .grid-toolbar-row, .grid-summary-row{ flex-shrink:0; }
        .grid-page > .my-2{ flex-shrink:0; }

        html:has(.grid-page), body:has(.grid-page), #root:has(.grid-page){
          height:100%; margin:0; overflow:hidden;
        }
        .grid-summary-row{
          display:grid !important;
          grid-template-columns:minmax(0,1fr) auto minmax(0,1fr);
          align-items:center; gap:12px;
        }
        .grid-summary-row > :last-child{ justify-self:end; }
        .grid-author-inline{ justify-self:center; text-align:center; font-size:13px; color:#555; }
        body.dark-mode .grid-author-inline{ color:#ddd; }
        /* Thin, rounded scrollbars like the FYI/cookie panel. */
        .grid-page, .grid-page *{
          scrollbar-width:thin;
          scrollbar-color:rgba(116,128,145,.48) transparent;
        }
        .grid-page::-webkit-scrollbar, .grid-page *::-webkit-scrollbar{ width:6px; height:6px; }
        .grid-page::-webkit-scrollbar-track, .grid-page *::-webkit-scrollbar-track{ background:transparent; }
        .grid-page::-webkit-scrollbar-thumb, .grid-page *::-webkit-scrollbar-thumb{
          background:rgba(116,128,145,.48); border-radius:20px;
        }
        .grid-page::-webkit-scrollbar-thumb:hover, .grid-page *::-webkit-scrollbar-thumb:hover{
          background:rgba(116,128,145,.72);
        }
        .grid-page::-webkit-scrollbar-button, .grid-page *::-webkit-scrollbar-button{
          display:none; width:0; height:0;
        }
        .grid-page::-webkit-scrollbar-corner, .grid-page *::-webkit-scrollbar-corner{ background:transparent; }
        body.dark-mode .grid-page, body.dark-mode .grid-page *{
          scrollbar-color:rgba(172,183,198,.5) transparent;
        }
        body.dark-mode .grid-page::-webkit-scrollbar-thumb,
        body.dark-mode .grid-page *::-webkit-scrollbar-thumb{ background:rgba(172,183,198,.5); }
        @media(max-width:900px){
          .grid-summary-row{ grid-template-columns:minmax(0,1fr) auto; }
          .grid-summary-row > .grid-author-inline{ grid-row:2; grid-column:1 / -1; }
        }

        /* Restore normal Clear button sizing with a small surrounding margin. */
        .grid-summary-row{
          margin-top:4px !important; min-height:31px;
          gap:8px; padding:0 24px;
        }
        .grid-page > .grid-card{ padding-bottom:4px !important; }

        /* Compact top controls while keeping equal margins around the buttons. */
        .grid-card{ padding:0 0 8px !important; }
        .grid-toolbar-row{
          padding:6.7px 24px; margin:0; min-height:48.4px;
          box-sizing:border-box; align-items:center;
        }

        /* Hide only the borders of the seven indicated header controls. */
        .grid-page table.grid-table thead th:is(
          [data-label="Status"], [data-label="User"], [data-label="Type"],
          [data-label="EP Status"], [data-label="Project"],
          [data-label="Notes Admin"], [data-label="Notes"]
        ) :is(.header-select, .header-input, .header-status-btn){
          border-color:transparent !important;
        }

        /* Subtle cell outlines for the table header. */
        body:not(.dark-mode) .grid-page table.grid-table thead th{
          border:1px solid #C9CFD7 !important;
        }
        body.dark-mode .grid-page table.grid-table thead th{
          border:1px solid #60666D !important;
        }

        /* Bold gray toolbar text and table headings. */
        .grid-table thead th,
        .grid-table thead th *,
        .grid-table thead th::after,
        .grid-topbar .grid-record-count,
        .grid-topbar .grid-record-count *,
        .grid-topbar .form-check-label{
          font-weight:700 !important;
        }
        body:not(.dark-mode) .grid-table thead th,
        body:not(.dark-mode) .grid-table thead .header-input,
        body:not(.dark-mode) .grid-table thead .header-select,
        body:not(.dark-mode) .grid-table thead .header-status-btn,
        body:not(.dark-mode) .grid-table thead th::after,
        body:not(.dark-mode) .grid-topbar .grid-record-count,
        body:not(.dark-mode) .grid-topbar .grid-record-count *,
        body:not(.dark-mode) .grid-topbar .form-check-label{
          color:#555555 !important;
        }
        body:not(.dark-mode) .grid-table thead input::placeholder{
          color:#555555 !important; font-weight:700 !important;
        }

        /* Regular weight for the labels marked in the toolbar and header. */
        .grid-page .grid-topbar .grid-record-count,
        .grid-page .grid-topbar .grid-record-count *,
        .grid-page .grid-topbar .form-check-label{
          font-weight:400 !important;
        }
        .grid-page table.grid-table thead th:is(
          [data-label="User"], [data-label="Type"], [data-label="EP Status"],
          [data-label="Created at"], [data-label="Project"], [data-label="Submission Date"],
          [data-label="OU-Comcast"], [data-label="Notes Admin"], [data-label="Notes"]
        ),
        .grid-page table.grid-table thead th:is(
          [data-label="User"], [data-label="Type"], [data-label="EP Status"],
          [data-label="Created at"], [data-label="Project"], [data-label="Submission Date"],
          [data-label="OU-Comcast"], [data-label="Notes Admin"], [data-label="Notes"]
        ) *,
        .grid-page table.grid-table thead th:is(
          [data-label="User"], [data-label="Type"], [data-label="EP Status"],
          [data-label="Created at"], [data-label="Project"], [data-label="Submission Date"],
          [data-label="OU-Comcast"], [data-label="Notes Admin"], [data-label="Notes"]
        )::after,
        body .grid-page table.grid-table thead th:is([data-label="Notes Admin"], [data-label="Notes"]) input::placeholder{
          font-weight:400 !important;
        }

        /* Darker gray for the record count and Ready for QC label. */
        body:not(.dark-mode) .grid-page .grid-topbar .grid-record-count,
        body:not(.dark-mode) .grid-page .grid-topbar .grid-record-count *,
        body:not(.dark-mode) .grid-page .grid-topbar .form-check-label{
          color:#444444 !important;
        }

        /* Header gray sampled from the left side of the banner. */
        body:not(.dark-mode) .grid-page table.grid-table thead th{
          background:#5C6069 !important; color:#F3F5F8 !important;
          border-color:#89909A !important;
          box-shadow:none;
        }
        body:not(.dark-mode) .grid-page table.grid-table thead th::after{
          color:#F3F5F8 !important;
        }
        body:not(.dark-mode) .grid-page table.grid-table thead :is(.header-input,.header-select,.header-status-btn):not(.header-jobid-yellow):not(.header-filter-active){
          background:#5C6069 !important; color:#F3F5F8 !important;
        }
        body:not(.dark-mode) .grid-page table.grid-table thead input:not(.header-jobid-yellow):not(.header-filter-active)::placeholder{
          color:#F3F5F8 !important;
        }
        body:not(.dark-mode) .grid-page table.grid-table thead .header-filter-active{
          background:#D6F3FF !important; color:#333 !important;
        }
        body:not(.dark-mode) .grid-page table.grid-table thead select option{
          background:#F3F5F7; color:#333;
        }

        /* Separate borders prevent collapsed-border repainting under sticky headers. */
        .grid-page table.grid-table{
          border-collapse:separate !important; border-spacing:0 !important;
        }
        .grid-page table.grid-table thead th{
          border-width:1px 1px 1px 0 !important;
          background-clip:border-box; opacity:1;
        }
        .grid-page table.grid-table thead th:first-child{ border-left-width:1px !important; }
        .grid-page table.grid-table tbody td,
        .grid-page table.grid-table tfoot td{ border-width:0 1px 1px 0; }
        .grid-page table.grid-table tbody td:first-child,
        .grid-page table.grid-table tfoot td:first-child{ border-left-width:1px; }

        /* Subtle gray distinguishes read-only dates and calculated totals. */
        body:not(.dark-mode) .grid-page table.grid-table tbody td.auto-cell{
          background-color:#f5f5f5;
        }
        body.dark-mode .grid-page table.grid-table tbody td.auto-cell{
          background-color:#444649 !important;
        }
        body.dark-mode .grid-page table.grid-table tbody tr.row-selected td.auto-cell{
          background:var(--row-selected-bg) !important;
        }
        body.dark-mode .grid-page table.grid-table tbody tr.row-selected:hover td.auto-cell{
          background:var(--row-selected-bg-hover) !important;
        }
        body.dark-mode .grid-page table.grid-table tbody tr.qc-flash td.auto-cell{
          background:#FFF3BF !important;
        }

        .grid-page table.grid-table .extra-hours-col{ width:110px; min-width:110px; }

        /* Frozen selection, favorite and Job Id columns, including header corners. */
        .grid-page table.grid-table.columns-frozen .frozen-col{
          position:sticky;
          background-clip:border-box;
        }
        .grid-page table.grid-table.columns-frozen .frozen-selection{ left:0; }
        .grid-page table.grid-table.columns-frozen .frozen-star{ left:var(--frozen-star-left, 50px); }
        .grid-page table.grid-table.columns-frozen .frozen-job{ left:var(--frozen-job-left, 100px); }
        .grid-page table.grid-table.columns-frozen tbody td.frozen-col{
          z-index:2;
          background-color:#fff;
        }
        .grid-page table.grid-table.columns-frozen thead th.frozen-col{ z-index:5; }
        /* Pastel blue identifies the three columns while freezing is enabled. */
        body .grid-page table.grid-table.columns-frozen tbody td.frozen-col{
          background:#D6F3FF !important;
          color:#34495E !important;
          --bs-table-bg:#D6F3FF;
          --bs-table-accent-bg:transparent;
          --bs-table-bg-state:transparent;
          --bs-table-bg-type:transparent;
        }
        .grid-page table.grid-table.columns-frozen .frozen-job{
          border-right-color:#89909A;
        }

        /* Keep the yellow Job Id label readable at rest, on hover and focus. */
        body:not(.dark-mode) .grid-page table.grid-table thead th[data-label="Job Id"]::after,
        body.dark-mode .grid-page table.grid-table thead th[data-label="Job Id"]::after{
          color:#555555 !important;
        }
        .grid-page table.grid-table thead .header-jobid-yellow,
        .grid-page table.grid-table thead .header-jobid-yellow::placeholder{
          color:#555555 !important;
        }

        /* Dark mode: keep Notes (starred) yellow */
        body.dark-mode .grid-table td.notes-starred{
          background:#FFF3BF !important;
          color:#3b2f00 !important;
        }
    `}</style>

      {/* HERO superior */}
      <div className="hero-wrap">
        <img className="hero-img" src={gridProjectBg} alt="" />
        <div className="brand-lockup" role="img" aria-label="Sans Frontières Design">
          <svg className="brand-emblem" viewBox="111 60 765 904" aria-hidden="true" focusable="false">
            <image href={sfdEmblem} width="1024" height="1024" />
          </svg>
          <svg className="brand-wordmark" viewBox="94 225 1563 470" aria-hidden="true" focusable="false">
            <image href={sfdBrand} width="1774" height="887" />
          </svg>
        </div>
        <div className="flowing-brand">
          <span className="flowing-mark" aria-hidden="true"><i /><i /><i /></span>
          <span>Flowing</span>
        </div>
      </div>



      <div className={`card shadow-sm p-4 grid-card${departureSession ? " departure-review-active" : ""}`}>
        <div className="d-flex justify-content-between align-items-center grid-toolbar-row">
          <h5 className="mb-0" style={{ color: "#555555", fontWeight: 700 }}>
            Projects - User: {loggedUser}
          </h5>
          <div className="d-flex gap-2 grid-topbar">
        <span className="grid-record-count"
          style={{ display:"inline-flex", alignItems:"center", gap:8, padding:"6px 14px", fontWeight:700 }}
          title="Total de registros visibles en el grid (según filtros)">
          <span>Total Records:</span>
          <span>{filteredRecords.length}</span>
        </span>

<div className="form-check form-switch d-flex align-items-center" title="Mostrar filas Ready for QC">
            <style id="readyforqc-switch-style">{`
              #toggle-readyforqc.form-check-input:checked {
                background-color: #FFB38A !important;
                border-color: #FFB38A !important;
              }
              #toggle-readyforqc.form-check-input:focus {
                box-shadow: 0 0 0 .25rem rgba(255, 179, 138, .35) !important;
                border-color: #FFB38A !important;
              }
            `}</style>

  <input
    id="toggle-readyforqc"
    className="form-check-input me-1"
    type="checkbox"
    checked={showArchived}
    onChange={(e)=>setShowArchived(e.target.checked)}
  />
  <label className="form-check-label" htmlFor="toggle-readyforqc" style={{fontWeight:600}}>Show Ready for QC</label>
</div>

<input
  type="checkbox"
  className="freeze-columns-toggle"
  checked={freezeColumns}
  onChange={(e) => setFreezeColumns(e.target.checked)}
  title={freezeColumns ? "Desfijar columnas de selección, estrella y Job ID" : "Fijar columnas de selección, estrella y Job ID"}
  aria-label="Fijar columnas de selección, estrella y Job ID"
  style={{ width:16, height:16, margin:"0 6px", cursor:"pointer", flexShrink:0, accentColor:"#FFB38A" }}
/>
<FyiMessages user={loggedUser} darkMode={darkMode} />
<span className="moon-toggle" role="button" onClick={() => setDarkMode(d => !d)} title={darkMode ? "Modo claro" : "Modo oscuro"}>
  <Icon name="moon" size={16} style={{ marginRight: 0 }} />
</span>
<span className="departure-envelope-wrap">
<button className={`btn btn-sm departure-download ${departureSession ? "btn-pastel-green" : "btn-pastel-blue"}`}
  onClick={downloadDeparture} disabled={departureLoading}
  aria-label={departureSession ? "Confirmar selección y generar DEPARTURE TXT" : "Detectar registros para DEPARTURE"}
  aria-describedby={departureHint && departureSession && !departureDialog ? "departure-help" : undefined}
  aria-busy={departureLoading}
  title={departureLoading ? "Procesando registros…" : departureSession ? "Revisa los checks y pulsa aquí para generar el TXT" : "Paso 1: detectar y marcar los registros para el cierre"}>
  {departureLoading ? <span aria-hidden="true" style={{ fontSize: 16 }}>⏳</span> : <Icon name="envelope" size={20} style={{ marginRight: 0, color: departureSession ? "#2E7D32" : "#29ABE2" }} />}
</button>
{departureHint && departureSession && !departureDialog && (
  <span className="departure-help" id="departure-help" role="status">
    <strong>Selección preparada: {departureChosen.length} registros seleccionados.</strong><br />
    Revisa los checks de la tabla y vuelve a pulsar el sobre para generar el TXT.
    <button type="button" className="departure-help-close" aria-label="Cerrar ayuda" onClick={() => setDepartureHint(false)}>×</button>
  </span>
)}
</span>
<button className="btn btn-sm btn-pastel-peach" onClick={openQcBell} title="Nuevos Ready for QC" style={{ position:"relative" }}>
              <Icon name="bell" size={16} />
              <span style={{ position:"absolute", top:-6, right:-6, minWidth:18, padding:"0 4px", borderRadius:12, background:"#E53935", color:"#fff", fontSize:12, lineHeight:"18px", textAlign:"center" }}>
                {qcCount}
              </span>
            </button>

<button
  className="btn btn-sm btn-pastel-peach ms-1"
  onClick={() => window.location.reload()}
  title="Refrescar (F5)"
  style={{ lineHeight: 0 }}
>
  <Icon name="refresh" size={22} style={{ marginRight: 0, verticalAlign: "middle" }} />
</button>

  {/* QC Bell */}
<button
              className="btn btn-add btn-sm"
              onClick={createNewLocalRecord}
              disabled={isCreating}
              title="Agregar fila temporal (arriba)"
            >
              <Icon name="plus" />Add
            </button>

            {/* Export XLS */}
            <button
              className="btn btn-sm btn-pastel-green"
              onClick={exportToXLS}
              title="Exportar registros filtrados a Excel"
            >
              <Icon name="excel" size={16} /> Download
            </button>

            {/* History */}
            <button className="btn btn-sm btn-pastel-blue" onClick={() => navigate("/history-ok")}>
              <Icon name="history" size={16} />History
            </button>

            


            <button className="btn btn-sm btn-pastel-purple" onClick={clearFilters}>
              <Icon name="filter-off" size={16} />Unfilter
            </button>
            <button
              className={`btn btn-sm ${(isDeleting || selectedRows.length === 0) ? 'btn-pastel-peach' : 'btn-pastel-red'}`}
              disabled={isDeleting || selectedRows.length === 0}
              onClick={async () => {
                if (selectedRows.length === 0) return;
                if (!window.confirm(`¿Borrar ${selectedRows.length} seleccionado(s)?`)) return;
                try {
                  setIsDeleting(true);
                  const { deleted } = await deleteProjectIds(selectedRows);
                  if (!deleted) {
                    pushToast("Linea eliminada No afectó el server.", "error");
                  } else {
                    pushToast(`Borrados ${deleted} registro(s).`, "success");
                    setSelectedRows([]);
                  }
                } catch (e) {
                  pushToast(`Error al borrar: ${e?.message || "desconocido"}`, "error");
                } finally {
                  setIsDeleting(false);
                }
              }}
            >
              <Icon name="trash" size={16} style={{ color: (isDeleting || selectedRows.length === 0) ? "#b69c94ff" : "#C62828" }} /> {isDeleting ? "Borrando…" : "Remove"}
            </button>
            <button
              className="btn btn-sm btn-solid-danger"
              onClick={() => {
                localStorage.removeItem("loggedUser");
                localStorage.removeItem("loggedWorker");
                sessionStorage.removeItem("loggedWorker");
                delete Axios.defaults.headers.common["X-Login-User"];
                delete Axios.defaults.headers.common["x-login-user"];
                navigate("/login", { replace: true });
      return;
              }}
            >
              <Icon name="logout" size={16} />Logout
            </button>
          </div>
        </div>

        {/* Área scroll */}
        <div className="grid-scroll" ref={scrollRef}>
          <table className={`table table-bordered table-hover grid-table${freezeColumns ? " columns-frozen" : ""}`}>
            <colgroup>
              <col style={{ width: "50px" }} />
              <col style={{ width: "50px" }} />
              <col style={{ width: "150px" }} />
              <col style={{ width: "140px" }} />
              <col style={{ width: "140px" }} />
              <col style={{ width: "170px" }} />
              <col style={{ width: "110px" }} />
              <col style={{ width: "180px" }} />
              <col style={{ width: "110px" }} />
              <col style={{ width: "180px" }} />
              <col style={{ width: "120px" }} />
              <col style={{ width: "200px" }} />
              <col style={{ width: "120px" }} />
              <col style={{ width: "120px" }} />
              <col style={{ width: "120px" }} />
              <col style={{ width: "120px" }} />
              <col style={{ width: "120px" }} />
              <col style={{ width: "120px" }} />
              <col style={{ width: "120px" }} />
              <col style={{ width: "120px" }} />
              <col style={{ width: "120px" }} />
              <col style={{ width: "120px" }} />
              <col style={{ width: "120px" }} />
              <col style={{ width: "120px" }} />
              <col style={{ width: "110px" }} />
            </colgroup>

            {/* Encabezado */}
            <thead>
              <tr>
                <th className="frozen-col frozen-selection">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={(e) => toggleSelectAll(e.target.checked)}
                    title="Seleccionar visibles"
                  />
                </th>
                <th className="frozen-col frozen-star">
                  <span title="Favoritos" style={{ fontSize: "18px" }}>☆</span>
                </th>

                {/* Job Id */}
                <th className="filter-input frozen-col frozen-job" data-label="Job Id">
                  <input type="search"
                    className="form-control form-control-sm header-input header-jobid-yellow"
                    placeholder="Job Id"
                    value={filters.jobid}
                    onChange={(e) => onFilterChange("jobid", e.target.value)}
                  />
                </th>

                <th className="filter-cell sortable" data-label="Status" onClick={() => onSort("jobsta")}>
                  <div ref={statusMenuRef} style={{ position:"relative" }} onClick={(e)=>e.stopPropagation()}>
  <button
    type="button"
    onClick={()=>setShowStatusMenu(v=>!v)}
    className={`header-status-btn${isHeaderFilterActive("status") ? " header-filter-active" : ""}`}
    style={{ fontSize:12, padding:"6px 40px", borderRadius:8, cursor:"pointer",
      border:"1px solid #CFD6DF", background: (isHeaderFilterActive("status") ? "#D6F3FF" : (darkMode ? "#424242" : "#E5E9EE")),
      color: (isHeaderFilterActive("status") ? "#111" : (darkMode ? "#fafafa" : "#333"))
      }}
  >
    {v2arr(filters.status).length ? `${v2arr(filters.status).length} seleccionados` : "Status"} ▾
  </button>

  {showStatusMenu && (
    <div style={{
      position:"absolute", zIndex:10, top:"100%", left:0, marginTop:4,
      background:(darkMode ? "#424242" : "#E5E9EE"), border:"1px solid #CFD6DF", borderRadius:10,
      boxShadow:"0 8px 20px rgba(0,0,0,.08)", padding:8, minWidth:160
    }}>
      <div style={{maxHeight:160, overflowY:"auto"}}>
        {statusOptions.map((opt)=> {
          const sel = v2arr(filters.status);
          const checked = sel.includes(opt);
          const toggle = () => onFilterChange("status", checked ? sel.filter(x=>x!==opt) : [...sel, opt]);
          return (
            <label key={opt} style={{display:"flex", gap:8, alignItems:"center", padding:"2px 6px", color:(darkMode ? "#F3F5F8" : "#444444")}}>
              <input type="checkbox" checked={checked} onChange={toggle}/>
              <span>{opt}</span>
            </label>
          );
        })}
      </div>
      <div style={{display:"flex", gap:8, justifyContent:"space-between", paddingTop:6}}>
        <button type="button"
          onClick={()=> onFilterChange("status", [])}
          style={{fontSize:12, padding:"2px 8px", borderRadius:8, border:"1px solid #CFD6DF", background:(darkMode ? "#3b3b3b" : "#E5E9EE")}}
        >Limpiar</button>
        <button type="button"
          onClick={()=> setShowStatusMenu(false)}
          style={{fontSize:12, padding:"2px 8px", borderRadius:8, border:"1px solid #CFD6DF", background:(darkMode ? "#3b3b3b" : "#E5E9EE")}}
        >Cerrar</button>
      </div>
    </div>
  )}
</div>
                <span className="sort-arrow">{sortKey === "jobsta" ? (sortDir === "asc" ? "▲" : "▼") : ""}</span></th>
{/* Notes */}
                


                <th className="filter-cell show-placeholder sortable" data-label="User" onClick={() => onSort("jobuser")}>
                  <select
                    className={`form-select form-select-sm header-select${isHeaderFilterActive("user") ? " header-filter-active" : ""}`}
                    value={filters.user}
                    onChange={(e) => onFilterChange("user", e.target.value)}
                   onClick={(e) => e.stopPropagation()}>
                    <option value="">All</option>
                    {userOptions.map((u) => (
                      <option key={u} value={u}>{u}</option>
                    ))}
                  </select>
                <span className="sort-arrow">{sortKey === "jobuser" ? (sortDir === "asc" ? "▲" : "▼") : ""}</span></th>

                <th className="filter-cell show-placeholder sortable" data-label="Type" onClick={() => onSort("jobti")}>
                  <select
                    className={`form-select form-select-sm header-select${isHeaderFilterActive("type") ? " header-filter-active" : ""}`}
                    value={filters.type}
                    onChange={(e) => onFilterChange("type", e.target.value)}
                   onClick={(e) => e.stopPropagation()}>
                    <option value="">All</option>
                    {typeOptions.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                <span className="sort-arrow">{sortKey === "jobti" ? (sortDir === "asc" ? "▲" : "▼") : ""}</span></th>

                <th className="filter-cell show-placeholder sortable" data-label="EP Status" onClick={() => onSort("jobclo")}>
                  <select
                    className={`form-select form-select-sm header-select${isHeaderFilterActive("closeep") ? " header-filter-active" : ""}`}
                    value={filters.closeep}
                    onChange={(e) => onFilterChange("closeep", e.target.value)}
                   onClick={(e) => e.stopPropagation()}>
                    <option value="">All</option>
                    {closeEpOptions.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                <span className="sort-arrow">{sortKey === "jobclo" ? (sortDir === "asc" ? "▲" : "▼") : ""}</span></th>
                <th className="filter-cell sortable" data-label="Created at" onClick={() => onSort("jobcreatedat")}>Created at<span className="sort-arrow">{sortKey === "jobcreatedat" ? (sortDir === "asc" ? "▲" : "▼") : ""}</span></th>

                <th className="filter-cell show-placeholder sortable" data-label="Project" onClick={() => onSort("jobpro")}>
                  <select
                    className={`form-select form-select-sm header-select${isHeaderFilterActive("project") ? " header-filter-active" : ""}`}
                    value={filters.project}
                    onChange={(e) => onFilterChange("project", e.target.value)}
                   onClick={(e) => e.stopPropagation()}>
                    <option value="">All</option>
                    {projectOptions.map((p) => (
                      <option key={p} value={p} className={optionClassForProject(p)}>
                        {p}
                      </option>
                    ))}
                  </select>
                <span className="sort-arrow">{sortKey === "jobpro" ? (sortDir === "asc" ? "▲" : "▼") : ""}</span></th><th className="filter-cell sortable" data-label="Submission Date" onClick={() => onSort("jobdatesub")}>Submission Date<span className="sort-arrow">{sortKey === "jobdatesub" ? (sortDir === "asc" ? "▲" : "▼") : ""}</span></th>

                <th style={{ fontWeight:700, color:"var(--th-color)" }} data-label="OU-Comcast">OU-Comcast</th>
                <th className="filter-input" data-label="Notes Admin">
                  <input type="search"
                    className={`form-control form-control-sm header-input${isHeaderFilterActive("notesadmin") ? " header-filter-active" : ""}`}
                    placeholder="Notes Admin"
                    value={filters.notesadmin}
                    onChange={(e) => onFilterChange("notesadmin", e.target.value)}
                  />
                </th>

                <th className="filter-input" data-label="Notes">
                  <input type="search"
                    className={`form-control form-control-sm header-input${isHeaderFilterActive("notes") ? " header-filter-active" : ""}`}
                    placeholder="Notes"
                    value={filters.notes}
                    onChange={(e) => onFilterChange("notes", e.target.value)}
                  />
                </th>{/* resto headers */}
                <th style={{ fontWeight: 700, color: "var(--th-color)" }}>Route Aerial</th>
                <th style={{ fontWeight: 700, color: "var(--th-color)" }}>Route UG</th>
                <th style={{ fontWeight: 700, color: "var(--th-color)" }}>Total Route</th>
                <th style={{ fontWeight: 700, color: "var(--th-color)" }}>Coax AE</th>
                <th style={{ fontWeight: 700, color: "var(--th-color)" }}>Coax UG</th>
                <th style={{ fontWeight: 700, color: "var(--th-color)" }}>Total Coax</th>
                <th style={{ fontWeight: 700, color: "var(--th-color)" }}>Asb AE</th>
                <th style={{ fontWeight: 700, color: "var(--th-color)" }}>Asb UG</th>
                <th style={{ fontWeight: 700, color: "var(--th-color)" }}>Total Asb</th>
                <th style={{ fontWeight: 700, color: "var(--th-color)" }}>Nodes/Sp Upd </th>
                <th style={{ fontWeight: 700, color: "var(--th-color)" }}>Amps/Sp New</th>
                <th className="extra-hours-col" title="Horas extras ingresadas manualmente">Hr Extras</th>
                 <th style={{ fontWeight: 700, color: "var(--th-color)" }} data-label="Project Name">Project Name</th>
                <th style={{ fontWeight: 700, color: "var(--th-color)" }} data-label="Project Port">Project Port</th>
                <th style={{ fontWeight: 700, color: "var(--th-color)" }} data-label="COM-Job type">COM-Job type</th>
                <th style={{ fontWeight: 700, color: "var(--th-color)" }} data-label="LOB">LOB</th></tr>
            </thead>

            {/* Body */}
            <tbody style={{ whiteSpace: "nowrap" }}>
              {sortedRecords.map((r, idx) => {
                const id = toId(r.jobid);
                const isFav = favoriteRows.includes(id);
                const isSel = selectedRows.includes(id);
                const rowNumber = idx + 1;

                const Cell = ({ field, value, pill, selectOptions, area, editor }) => {
                  const isEditing =
                    editingCell && editingCell.id === id && editingCell.field === field;

                  const editable = editableFields.has(field);
                  const start = () => { if (editable) { suppressAutoOpenRef.current = false; startEdit(id, field, value); } };
                  const onKey = (e) => {
                    if (e.key === "Enter") commitEdit();
                    if (e.key === "Escape") cancelEdit();
                  };

                  // Si está vacía, permite entrar con un clic
                  const clickStartIfEmpty = () => {
                    const empty = value === "" || value === null || value === undefined;
                    if (editable && empty) startEdit(id, field, value);
                  };

                  // Refs y estado local para modal
                  const areaRef = useRef(null);
                  const inputRef = useRef(null);
                  const selectRef = useRef(null);
                  const suppressAutoOpenRef = useRef(false);


                  const [localValue, setLocalValue] = useState(editValue);

                 // useEffect(() => {
                 //   if (isEditing && ["notes", "proname", "proport", "ou", "lob"].includes(area)) {
                 //     setLocalValue(editValue ?? "");
                 //   }
                 // }, [isEditing, editValue, area]);

                 useEffect(() => {
  if (isEditing && ["notes","notesadmin","proname","proport","ou","lob","comty"].includes(area)) {
    setLocalValue(value ?? "");
  }
}, [isEditing, value, area]);

                  useEffect(() => {
                    if (!isEditing) return;
                   if (["notes", "notesadmin", "proname", "proport", "ou", "lob", "comty"].includes(area)) {
                      if (areaRef.current) {
                        const el = areaRef.current;
                        requestAnimationFrame(() => {
                          try {
                            const L = el.value.length;
                            el.setSelectionRange(L, L);
                            el.focus();
                          } catch {}
                        });
                      }
                    } else {
                      if (inputRef.current) {
                        // 🔧 FIX: no seleccionar todo; colocar el caret al final.
                        try {
                          const L = inputRef.current.value.length;
                          inputRef.current.setSelectionRange(L, L);
                          inputRef.current.focus();
                        } catch {}
                      }
                    }
                  }, [isEditing, area]);
                  useEffect(() => { if (isEditing && selectOptions && selectRef.current && !suppressAutoOpenRef.current) {
                      const el = selectRef.current;
                      requestAnimationFrame(() => {
                        try {
                          el.focus();
                          if (typeof el.showPicker === "function") {
                            el.showPicker();
                          } else {
                            // Fallback: simulate a mouse down to open the native select
                            const evt = new MouseEvent("mousedown", { bubbles: true, cancelable: true, view: window });
                            el.dispatchEvent(evt);
                          }
                        } catch {}
                      });
                    }
                  }, [isEditing, selectOptions]);


                  // === Select
                  if (isEditing && selectOptions) {
                    return (
                      <select
                        autoFocus
                        ref={selectRef}
                        className="form-select form-select-sm"
                        value={editValue ?? ""}
                        onChange={(e) => { const v = e.target.value; suppressAutoOpenRef.current = true; setEditValue(v); requestAnimationFrame(() => { try { if (selectRef.current) selectRef.current.blur(); } catch {} commitEdit(v); }); }}
                        onBlur={() => setTimeout(() => commitEdit(), 0)}
                        onKeyDown={onKey}
                      >
                        <option value="">— Seleccionar —</option>
                        {selectOptions.map((opt) => (
                          <option
                            key={opt}
                            value={opt}
                            className={pill === "project" ? optionClassForProject(opt) : undefined}
                          >
                            {opt}
                          </option>
                        ))}
                      </select>
                    );
                  }

     
              // === Edición con MODAL (Notes / Project Name / Project Port / OU / LOB / OU-Comcast)
 if (isEditing && ["notes","notesadmin","proname","proport","ou","lob","comty"].includes(area)) {
                const stop = (e) => e.stopPropagation();

                const save = () => commitEdit(localValue);

                const escOrSave = (e) => {
                  if (e.key === "Escape") cancelEdit();
                  if ((e.ctrlKey || e.metaKey) && e.key === "Enter") save();
                };

                  const titleMap = { notes:"Editar Notes", proname:"Editar Project Name", proport:"Editar Project Port", ou:"Editar OU", lob:"Editar LOB", comty:"Editar OU-Comcast" };
 const height = area === "notes" ? "50vh" : (area === "comty" ? "40vh" : "30vh");
                return (
                  <>
                    <div
                      onClick={cancelEdit}
                      style={{
                        position: "fixed", inset: 0, background: "rgba(0,0,0,.35)", zIndex: 9998
                      }}
                    />
                    <div
                      onClick={stop}
                      style={{
                        position: "fixed", zIndex: 9999,
                        left: "50%", top: "10vh", transform: "translateX(-50%)",
                        width: "min(900px, 90vw)", background: "#fff",
                        borderRadius: 12, boxShadow: "0 12px 28px rgba(0,0,0,.2)",
                        padding: 12
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <strong>{titleMap[area] || "Editar"}</strong>
                        <button className="btn btn-sm btn-outline-secondary" onClick={cancelEdit}>×</button>
                      </div>

                      <textarea
                        ref={areaRef}
                        autoFocus
                        className="form-control"
                        style={{ height }}
                        value={localValue}
                        onChange={(e) => setLocalValue(e.target.value)}
                        onKeyDown={escOrSave}
                        placeholder="Escribe aquí…  (Ctrl+Enter para guardar)"
                      />

                      <div className="d-flex justify-content-end gap-2 mt-2">
                        <button className="btn btn-secondary btn-sm" onClick={cancelEdit}>Cancelar</button>
                        <button className="btn btn-primary btn-sm" onClick={save}>Guardar</button>
                      </div>
                    </div>
                  </>
                );
              }


                  // Decimal text input accepts both separators without changing other editors.
                  if (isEditing && field === "jbhrex") {
                    return (
                      <input
                        ref={inputRef}
                        autoFocus
                        type="text"
                        inputMode="decimal"
                        className="form-control form-control-sm"
                        defaultValue={String(editValue ?? "")}
                        aria-label="Hr Extras"
                        title="Solo números no negativos; decimales con punto (.) o coma (,). Vacío equivale a 0."
                        onBlur={(e) => { if (e.currentTarget.dataset.cancelled !== "1") commitEdit(e.currentTarget.value); }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); }
                          if (e.key === "Escape") { e.preventDefault(); e.currentTarget.dataset.cancelled = "1"; cancelEdit(); }
                        }}
                      />
                    );
                  }

                  // === Input especial para JobID (no controlado)  🔧 FIX
                  if (isEditing && field === "jobid") {
                    return (
                      <input
                        ref={inputRef}
                        autoFocus
                        className="form-control form-control-sm"
                        defaultValue={editValue}
                        onBlur={(e) => commitEdit(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") commitEdit(e.currentTarget.value);
                          if (e.key === "Escape") cancelEdit();
                        }}
                      />
                    );
                  }

                  // === Input normal controlado
                  if (isEditing) {
                    return (
                      <input
                        ref={inputRef}
                        autoFocus
                        className="form-control form-control-sm"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={() => setTimeout(() => commitEdit(), 0)}
                        onKeyDown={onKey}
                      />
                    );
                  }

                  // === Visualización
                  const classForPill =
                    pill === "status"
                      ? statusPillClass(value)
                      : pill === "user"
                      ? "pill pill-user"
                        : pill === "type" ? typePillClass(value)

                      : pill === "closeep" ? epPillClass(value)

                      : pill === "project"
                      ? "pill pill-project"


                      : null;

                  let display = value ?? "";
                  if (field === "jbhrex") display = formatExtraHours(value);
                  if (editor === "date") display = field === "jobdatesub" ? formatDateTimeHMLima(value) : formatDateTime(value);
                  const isEmpty = display === "" || display === null || display === undefined;

                  if (classForPill) {
                    const extraStyle = pill === "project" ? projectStyle(value) : undefined;
                    return (
                      <div
                        className={editable ? "cell-editable" : undefined}
                        onClick={editable ? start : undefined}
                        title={editable ? "Click para editar" : undefined}
                      >
                        {isEmpty ? (
                          <span className="cell-empty">-</span>
                        ) : (
                          <span className={classForPill} style={extraStyle}>
                            {display}
                          </span>
                        )}
                      </div>
                    );
                  }

                  // --- Clamp para Notes y Project Name (una sola línea, no toca otras celdas) ---
                  const contentNode = isEmpty
                    ? <span className="cell-empty">-</span>
                    : ((area === "notes" || area === "proname" || area === "comty" || area === "lob")
                        ? <span className="notes-clip" title={String(display)}>{display}</span>
                        : display);

                  return (
                    <div
                      className={editable ? "cell-editable" : undefined}
                        onClick={editable ? start : undefined}
                        title={editable ? "Click para editar" : undefined}
                    >
                      {contentNode}
                    </div>
                  );
                };

                return (
                  <tr key={id} data-rowid={id} className={(isSel ? "row-selected " : "") + (flashRowId===id ? "qc-flash" : "")}>
                    {/* Selección: índice → checkbox en hover (o fijo si row-selected) */}
                    <td className="sel-cell frozen-col frozen-selection">
                      <span className="row-index">{rowNumber}</span>
                      <input
                        type="checkbox"
                        className="row-check"
                        checked={isSel}
                        onChange={(e) => handleCheckboxChange(e, id)}
                        title={`Seleccionar fila ${rowNumber}`}
                      />
                    </td>

                    {/* Estrella naranja */}
                    <td className="frozen-col frozen-star">
                      <span
                        className={`fav-star ${isFav ? "on" : "off"}`}
                        onClick={() => toggleFavorite(id)}
                        title={isFav ? "Quitar de favoritos" : "Marcar como favorito"}
                        role="button"
                        aria-label={isFav ? "Quitar de favoritos" : "Marcar como favorito"}
                      >
                        {isFav ? "★" : "☆"}
                      </span>
                    </td>

                    {/* Datos */}
                    <td className="left-cell frozen-col frozen-job"><Cell field="jobid" value={r.jobid} /></td>
                    <td className="left-cell status-cell" style={{textAlign:"center"}}><Cell field="jobsta" value={r.jobsta} pill="status" selectOptions={statusOptions} /></td>
                    

                    <td className="left-cell user-cell"><Cell field="jobuser" value={r.jobuser} pill="user" selectOptions={userOptions} /></td>
                    <td className="left-cell" style={{textAlign:"center"}}><Cell field="jobti" value={r.jobti} pill="type" selectOptions={typeOptions} /></td>
                    <td className="left-cell" style={{textAlign:"center"}}><Cell field="jobclo" value={r.jobclo} pill="closeep" selectOptions={closeEpOptions} /></td>
                    <td className="auto-cell">{formatDateTimeHMLima(r.jobcreatedat || "")}</td>
                    <td className="left-cell" style={{textAlign:"center"}}><Cell field="jobpro" value={r.jobpro} pill="project" selectOptions={projectOptions} /></td><td className="auto-cell"><Cell field="jobdatesub" value={r.jobdatesub} editor="date" /></td> {/* NO editable */}
                    <td className="wide-col clip-cell"><div className="clip-flex"><div className="clip-text"><Cell field="jobcomty"value={r.jobcomty} area="comty" /></div></div></td>
                    <td className={`left-cell wide-col clip-cell ${isFav ? "notes-starred" : ""}`}><div className="clip-flex"><div className="clip-text"><Cell field="jobnotesadmin" value={r.jobnotesadmin} area="notesadmin" /></div></div></td><td className={`left-cell wide-col clip-cell ${isFav ? "notes-starred" : ""}`}><div className="clip-flex"><div className="clip-text"><Cell field="jobnotes" value={r.jobnotes} area="notes" /></div></div></td>
                    <td><Cell field="jobrouteaerial" value={r.jobrouteaerial} /></td>
                    <td><Cell field="jobrouteug" value={r.jobrouteug} /></td>
                    <td className="auto-cell"><div>{(parseFloat(r.jobrouteaerial)||0) + (parseFloat(r.jobrouteug)||0)}</div></td>
                    <td><Cell field="jobcoaxae" value={r.jobcoaxae} /></td>
                    <td><Cell field="jobcoaxug" value={r.jobcoaxug} /></td>
                    <td className="auto-cell"><div>{(parseFloat(r.jobcoaxae)||0) + (parseFloat(r.jobcoaxug)||0)}</div></td>
                    <td><Cell field="jobasbae" value={r.jobasbae} /></td>
                    <td><Cell field="jobasbug" value={r.jobasbug} /></td>
                    <td className="auto-cell"><div>{(parseFloat(r.jobasbae)||0) + (parseFloat(r.jobasbug)||0)}</div></td>
                    <td><Cell field="joblode" value={r.joblode} /></td>
                    <td><Cell field="jobhc" value={r.jobhc} /></td>
                    <td className="extra-hours-col"><Cell field="jbhrex" value={r.jbhrex} /></td>
                    {/* movidos al final */}
                    <td className="project-name-col clip-cell"><div className="clip-flex"><div className="clip-text"><Cell field="jobproname" value={r.jobproname} area="proname" /></div></div></td>
                    <td className="project-port-col clip-cell"><div className="clip-flex"><div className="clip-text"><Cell field="jobproport" value={r.jobproport} area="proport" /></div></div></td>
                    <td className="com-job-type-col clip-cell"><div className="clip-flex"><div className="clip-text"><Cell field="jobou" value={r.jobou} area="ou" /></div></div></td>
                    <td className="lob-col clip-cell"><div className="clip-flex"><div className="clip-text"><Cell field="joblob" value={r.joblob} area="lob" /></div></div></td></tr>
                );
              })}
            </tbody>

            <tfoot>
              <tr>
                <td colSpan={29} style={{ textAlign: "left", background: "#f7f8fd" }}>
                  <small className="ms-2 text-muted">
                    Las filas con <em>JobID nuevo-#</em> son temporales; al cambiar el Job Id por uno real, se guardan en el servidor.
                  </small>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div className="d-flex justify-content-between align-items-center mt-3 grid-summary-row">
          <small className="text-muted">
            {filteredRecords.length} registros · {selectedRows.length} seleccionados · {favoriteRows.length} favoritos
          </small>
          <span className="grid-author-inline">Created by NelsonSQC © V.2.7</span>
          <div className="d-flex gap-2">
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => setSelectedRows([])}
              disabled={selectedRows.length === 0}
            >
              <Icon name="x-circle" />Clear Selected</button>
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => setFavoriteRows([])}
              disabled={favoriteRows.length === 0}
            >
              <Icon name="star-off" />Clear Favorites
            </button>
          </div>
        </div>
      </div>

      {departureSession && !departureDialog && (
        <div className="d-flex align-items-center gap-2 my-2" style={{ flexWrap: "wrap" }}>
          <span>DEPARTURE: {departureChosen.length} registros seleccionados · {departureAutoCount} automáticos · {departureManualCount} manuales. Ajusta los checks y pulsa el sobre para generar el TXT.</span>
          <button type="button" className="btn btn-sm btn-pastel-blue" onClick={() => { setDepartureSession(null); setSelectedRows([]); }}>Nueva detección</button>
        </div>
      )}
      {departureDialog && departureSession && (
        <div className="departure-dialog-overlay">
          <section className="departure-dialog" role="dialog" aria-modal="true" aria-labelledby="departure-dialog-title">
            <h5 id="departure-dialog-title">Generar reporte DEPARTURE</h5>
            <p><strong>{departureChosen.length} registros seleccionados</strong>: {departureAutoCount} automáticos y {departureManualCount} manuales.</p>
            <p>¿Generar el reporte con los registros marcados en la tabla?</p>
            <p>Los completados incluidos seguirán apareciendo hasta las 4 a. m. Puedes generar el reporte varias veces.</p>
            <div className="d-flex gap-2 mt-3" style={{ flexWrap: "wrap" }}>
              <button className="btn btn-sm btn-pastel-green" disabled={departureLoading} onClick={executeDeparture}>{departureLoading ? "Generando…" : "Generar reporte"}</button>
              <button className="btn btn-sm btn-pastel-gray" disabled={departureLoading} onClick={() => setDepartureDialog(false)}>No / Revisar en tabla</button>
              <button className="btn btn-sm btn-pastel-blue" disabled={departureLoading} onClick={() => { setDepartureDialog(false); setDepartureSession(null); setSelectedRows([]); }}>Cancelar</button>
            </div>
          </section>
        </div>
      )}

      {/* QC Post-it */}
      {showQcPostit && (
        <div className="qc-postit" style={{ position:"fixed", right:20, top:100, width:300, zIndex:9999, transform:"rotate(1.2deg)" }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 10px", fontWeight:700 }}>
            <span>🔔 Ready for QC ({qcList.length})</span>
            <button className="btn btn-sm btn-light" onClick={markQcRead} title="Marcar como leído">OK</button>
          </div>
          <div style={{ padding:"4px 10px 10px" }}>
            {qcList.length === 0 ? (
              <div style={{ opacity:.8, fontStyle:"italic" }}>No hay nuevos avisos.</div>
            ) : (
              <ul style={{ listStyle:"none", margin:0, padding:0, maxHeight:240, overflowY:"auto" }}>
                {qcList.map((it, i) => (
                  <li key={(it.id||i) + "-qc"} style={{ padding:"6px 0", borderTop: i? "1px dashed #e3cc8a":"none", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                    <div>
                      <div><strong>{it.id} Listo {it.project || (records.find(r => String(r?.jobid)===String(it.id))?.jobpro || "")}</strong></div>
                    </div>
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => gotoQcItem(it.id)}>Ver</button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
{/* Toasts */}
      <div style={{position:"fixed", right:16, bottom:16, zIndex:9999}}>
        {toasts.map(t => (
          <div
            key={t.id}
            className="shadow-sm mb-2 px-3 py-2 rounded text-white"
            style={{
              background: t.variant==="success" ? "#2e7d32" : t.variant==="error" ? "#c62828" : "#6c757d",
              position: "relative",
              paddingRight: 28
            }}
          >
            <span>{t.msg}</span>
            <button
              type="button"
              aria-label="Cerrar"
              title="Cerrar"
              onMouseDown={(e)=>e.preventDefault()}
              onClick={() => closeToast(t.id)}
              style={{
                position: "absolute",
                top: 4,
                right: 6,
                border: "none",
                background: "transparent",
                color: "white",
                fontWeight: 700,
                cursor: "pointer",
                lineHeight: 1,
                fontSize: 16
              }}
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
export default GridPage;